import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  api,
  type ConnectedAccount,
  type DispatchResult,
  type Draft,
  type MediaKind,
  type PerPlatformOverride,
  type PlatformId,
  type PlatformInfo,
  type PostDraft,
  type ScheduledPost,
  type Snippet,
  type TikTokCreatorInfo,
  type TikTokOptions,
  type Visibility,
  type YouTubeOptions,
} from "../api";
import {
  parseFolder,
  platformsInPost,
  postVisibility,
  toDatetimeLocal,
  type ImportedPost,
} from "../lib/folderImport";
import {
  buildBatchTimes,
  DEFAULT_PER_DAY,
  defaultSlotTimes,
  timeToMinutes,
  type PerDay,
} from "../lib/smartSchedule";
import { creditCostForPlatforms, formatCredits } from "../lib/credits";
import { epochToInputValue, formatInTz, wallClockToEpoch } from "../lib/tz";
import { ImportHelp } from "./ImportHelp";
import { type BusyPost } from "./ScheduleStrip";
import { SchedulePicker } from "./SchedulePicker";
import { InfoHint } from "./InfoHint";
import { PlatformIcon } from "./PlatformIcon";
import {
  buildTikTokOptions,
  EMPTY_TIKTOK_SETTINGS,
  tiktokMaxDurationSec,
  TikTokPanel,
  tiktokSettingsBlocker,
  type TikTokSettings,
} from "./TikTokPanel";
import {
  EMPTY_YOUTUBE_SETTINGS,
  madeForKidsValue,
  YouTubePanel,
  YouTubeUploadNotice,
  youtubeLimitBlocker,
  youtubeLimitProblems,
  youtubeSettingsBlocker,
  type YouTubeDraftText,
  type YouTubeSettings,
} from "./YouTubePanel";
import {
  alwaysPublicSelected,
  EMPTY_VISIBILITY,
  PublicOnlyNotice,
  VisibilityPicker,
  visibilityBlocker,
  type VisibilityChoice,
} from "./VisibilityPanel";
import { formatDuration, VideoPreview } from "./VideoPreview";
import {
  humanizeDispatchError,
  isInsufficientCreditsError,
  isPendingPlatformError,
} from "../lib/errorMessage";

// The known PlatformId set, used to VALIDATE server-supplied platform keys before
// casting them to PlatformId. The server is trusted, but an unknown key slipping
// through `as PlatformId` would poison the selection/override maps with an id no
// adapter handles — so anything not in this set is dropped, not cast.
const PLATFORM_IDS: ReadonlySet<PlatformId> = new Set<PlatformId>([
  "youtube",
  "tiktok",
  "instagram",
  "x",
  "facebook",
]);
function isPlatformId(value: string): value is PlatformId {
  return PLATFORM_IDS.has(value as PlatformId);
}

/** Every platform id in a stable display order — the batch platform pickers and
 * the per-row caption boxes iterate this. */
const ALL_PLATFORM_IDS: readonly PlatformId[] = [
  "youtube",
  "tiktok",
  "instagram",
  "x",
  "facebook",
];

/** Platforms whose short-video surface (Shorts / Reels) rewards a native
 * trending sound — the ones the "add a sound in the app" hint is for. X is
 * excluded (no equivalent sound feature). */
const SOUND_HINT_PLATFORMS = new Set<PlatformId>(["youtube", "tiktok", "instagram", "facebook"]);

/** Seed the batch's per-platform caption boxes (item A) from a post's sidecar —
 * the non-TikTok caption each platform declares. TikTok keeps its own box, so
 * it's excluded here. A bare folder returns an empty map (all boxes start blank). */
function seedBatchText(post: ImportedPost): Partial<Record<PlatformId, string>> {
  const p = post.data.platforms ?? {};
  const out: Partial<Record<PlatformId, string>> = {};
  if (p.youtube?.title) out.youtube = p.youtube.title;
  if (p.instagram?.caption) out.instagram = p.instagram.caption;
  if (p.x?.text) out.x = p.x.text;
  if (p.facebook?.caption) out.facebook = p.facebook.caption;
  return out;
}

/** A one-line idea to seed a row's AI caption generation: the richest caption the
 * sidecar already holds, or a humanised version of the folder name for a bare
 * folder (so even "post_03_bench_press" gives the model something to expand). */
function seedBatchIdea(post: ImportedPost): string {
  const p = post.data.platforms ?? {};
  const best =
    p.youtube?.title ??
    p.tiktok?.caption ??
    p.instagram?.caption ??
    p.x?.text ??
    p.facebook?.caption;
  if (best && best.trim()) return best.trim();
  return post.folder
    .replace(/^post[_-]?\d*[_-]?/i, "")
    .replace(/[_-]+/g, " ")
    .trim();
}

/** Editable AI-tailored caption for one platform (tags kept as a raw string
 * while the creator edits; split into an array only at publish time). */
interface AiEdit {
  title: string;
  body: string;
  tags: string;
}

/** One field in a platform's review card. */
interface AiCardField {
  key: keyof AiEdit;
  label: string;
  multiline: boolean;
}

/**
 * Which override fields a platform's review card shows, and how to label them.
 * Each platform uses the AiEdit fields differently — YouTube has a title + a
 * description; TikTok/IG/FB/X have only a caption, and TikTok publishes it from
 * the `title` field while IG/FB/X publish it from `body` (see _lib/ai.ts). A
 * uniform Title/Caption/Tags layout therefore mislabels most of them and hides
 * a platform's real text under the wrong heading (or an empty one). This maps
 * each platform to just the fields it actually uses.
 */
function aiCardFields(id: PlatformId): AiCardField[] {
  switch (id) {
    case "youtube":
      return [
        { key: "title", label: "Title", multiline: false },
        { key: "body", label: "Description", multiline: true },
        { key: "tags", label: "Tags (comma-separated)", multiline: false },
      ];
    case "tiktok":
      // TikTok publishes the `title` field as the on-video caption.
      return [
        { key: "title", label: "Caption", multiline: true },
        { key: "tags", label: "Hashtags (comma-separated)", multiline: false },
      ];
    case "instagram":
      return [
        { key: "body", label: "Caption", multiline: true },
        { key: "tags", label: "Hashtags (comma-separated)", multiline: false },
      ];
    case "facebook":
      return [
        { key: "body", label: "Caption", multiline: true },
        { key: "tags", label: "Hashtags (comma-separated)", multiline: false },
      ];
    case "x":
      // X is text-only today — one body, no title, no separate hashtag field.
      return [{ key: "body", label: "Post text", multiline: true }];
    default:
      return [
        { key: "title", label: "Title", multiline: false },
        { key: "body", label: "Caption", multiline: true },
        { key: "tags", label: "Tags", multiline: false },
      ];
  }
}

// Kept in sync with MAX_UPLOAD_BYTES on the server (2 GB). The server re-checks;
// this is just a fast local guard so we don't presign an obviously bad upload.
const MAX_UPLOAD_BYTES = 2 * 1024 * 1024 * 1024;

// Concrete allowlist mirroring ALLOWED_CONTENT_TYPES on the server. The server
// re-validates; this is just a fast local guard (and excludes image/svg+xml).
const ALLOWED_CONTENT_TYPES = new Set<string>([
  "video/mp4",
  "video/quicktime",
  "video/webm",
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
]);

// Meta accepts JPEG as the ONLY supported image format for Instagram (Instagram
// Platform → Content Publishing). A PNG/WebP/GIF image sent to Instagram fails at
// Meta with an opaque error, so the composer catches the Instagram + non-JPEG
// image case at compose time instead of letting it fail downstream. Videos
// (Reels) are unaffected — this is images only.
const IMAGE_NAME_EXT = /\.(jpe?g|png|webp|gif)$/i;

/** Whether a content type and/or filename/URL denotes a JPEG. A known image
 * content type is conclusive; otherwise the extension settles it. */
function looksJpeg(type: string | null | undefined, ref: string): boolean {
  const t = (type ?? "").toLowerCase();
  if (t === "image/jpeg" || t === "image/jpg") return true;
  if (t.startsWith("image/")) return false; // a known, non-JPEG image type
  return /\.jpe?g(?:[?#]|$)/i.test(ref);
}

/** True when a picked file is an image that is NOT JPEG — the one combination
 * Instagram rejects (see above). Videos and JPEGs return false. Used by the
 * batch per-row Instagram skip. */
function fileIsNonJpegImage(file: File | null): boolean {
  if (!file) return false;
  const type = file.type.toLowerCase();
  const name = file.name.toLowerCase();
  const isImage = type.startsWith("image/") || IMAGE_NAME_EXT.test(name);
  if (!isImage) return false;
  return !looksJpeg(type, name);
}

/** PUT a file directly to a presigned URL, reporting upload progress. Uses
 * XMLHttpRequest because fetch can't report upload progress. */
function putWithProgress(
  url: string,
  file: File,
  headers: Record<string, string>,
  onProgress: (pct: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    for (const [k, v] of Object.entries(headers)) {
      // content-length is a forbidden header: the browser sets it from the body
      // automatically to match the signed value. Trying to set it here is a
      // no-op at best and throws in some browsers, so skip it.
      if (k.toLowerCase() === "content-length") continue;
      xhr.setRequestHeader(k, v);
    }
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`Upload failed (${xhr.status}).`));
    xhr.onerror = () => reject(new Error("Network error during upload."));
    xhr.send(file);
  });
}

// Format a Date as the value a <input type=datetime-local> expects, in LOCAL
// time ("YYYY-MM-DDTHH:mm"). Mirrors toDatetimeLocal but for a live Date.
function toInputValue(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

// Reverse of `buildTikTokOptions`: recover the panel's settings shape from a
// saved draft's wire options so editing a scheduled post shows the choices it
// was scheduled with. Account-level `*_disabled` flags are re-applied live by
// the panel (a greyed control still forces its interaction off), so mapping the
// stored booleans straight back is safe.
function tiktokSettingsFromOptions(opts: TikTokOptions): TikTokSettings {
  return {
    privacyLevel: opts.privacyLevel,
    allowComment: !opts.disableComment,
    allowDuet: !opts.disableDuet,
    allowStitch: !opts.disableStitch,
    disclose: opts.commercialContent,
    yourBrand: opts.yourBrand,
    brandedContent: opts.brandedContent,
  };
}

// Per-post lifecycle status while a batch is being scheduled.
type BatchState = "pending" | "uploading" | "scheduling" | "publishing" | "done" | "failed";
interface BatchRowStatus {
  state: BatchState;
  error?: string;
}
const BATCH_STATUS_LABEL: Record<BatchState, string> = {
  pending: "pending",
  uploading: "uploading…",
  scheduling: "scheduling…",
  publishing: "publishing…",
  done: "done ✓",
  failed: "failed",
};

/** The idempotency key one imported post is sent under.
 *
 * It must be DETERMINISTIC for a given post within a given import, or a retry
 * is not a retry: the server dedupes on this key alone (schedule.mts /
 * dispatch.mts), so a fresh key per press turns "send the ones that failed"
 * into "send everything again, undetectably".
 *
 * `runId:index` is already unique per post per import; the folder is appended
 * only so a stored key is legible. The server discards keys over 100 chars
 * (normalizeIdempotencyKey) — which would silently disable dedupe — so the
 * whole thing is trimmed to fit. Trimming only ever cuts into the cosmetic
 * folder tail, so it cannot make two posts collide.
 */
function batchIdempotencyKey(runId: string, index: number, folder: string): string {
  return `${runId}:${index}:${folder}`.slice(0, 100);
}

// Batch cadence choices. 1/day is the default: it reads better to followers and
// halves the draw on YouTube's shared daily upload quota.
const BATCH_CADENCES: { perDay: PerDay; label: string }[] = [
  { perDay: 1, label: "1 per day (recommended)" },
  { perDay: 2, label: "2 per day" },
];

// "Sat, 8 Aug" — enough to judge how far out a batch runs, in the user's locale.
const END_DATE_FORMAT = new Intl.DateTimeFormat(undefined, {
  weekday: "short",
  day: "numeric",
  month: "short",
});

/** The TikTok caption a batch row starts with, before the creator edits it. */
function importedTiktokCaption(post: ImportedPost): string {
  return post.data.platforms?.tiktok?.caption ?? "";
}

/** A saved draft's display label: its own title, else the draft's title, else a
 * placeholder — a draft is saveable with no title at all. */
function draftLabel(d: Draft): string {
  return d.title?.trim() || d.draft.title?.trim() || "Untitled draft";
}

/**
 * The unobtrusive "Saved" dropdown shown beside the caption and tags fields. A
 * `<details>` element so it needs no open-state or outside-click handling: one
 * action to save what's currently in the field, and a click-to-insert list of
 * saved snippets of this kind. Deliberately small — a button, not a panel.
 */
function SavedMenu({
  label,
  snippets,
  onInsert,
  onSaveCurrent,
  onDelete,
  emptyHint,
}: {
  /** Human word for this snippet kind, e.g. "caption" or "hashtag group". */
  label: string;
  snippets: Snippet[];
  onInsert: (content: string) => void;
  onSaveCurrent: () => void;
  onDelete: (id: string) => void;
  emptyHint: string;
}) {
  return (
    <details className="saved-menu">
      <summary className="saved-menu-toggle">Saved</summary>
      <div className="saved-menu-panel card">
        <button type="button" className="btn small" onClick={onSaveCurrent}>
          Save current {label}
        </button>
        {snippets.length === 0 ? (
          <p className="muted small">{emptyHint}</p>
        ) : (
          <ul className="saved-menu-list">
            {snippets.map((s) => (
              <li key={s.id} className="saved-menu-item">
                <button
                  type="button"
                  className="saved-menu-insert"
                  title={s.content}
                  onClick={() => onInsert(s.content)}
                >
                  {s.name}
                </button>
                <button
                  type="button"
                  className="saved-menu-del"
                  aria-label={`Delete ${s.name}`}
                  onClick={() => onDelete(s.id)}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </details>
  );
}

export function Composer({
  platforms,
  accounts,
  uploadConfigured,
  aiConfigured,
  timezone,
  editPost,
  repostFrom,
  onExitEdit,
  scheduled,
  workspaceId,
  onDispatched,
  creditsBalance,
  onBuyCredits,
  onPublishInFlight,
  onViewActivity,
  onViewCalendar,
  onHide,
  onConnectAccounts,
}: {
  platforms: PlatformInfo[];
  accounts: ConnectedAccount[];
  uploadConfigured: boolean;
  aiConfigured: boolean;
  /** The effective IANA timezone: every schedule time is interpreted AND
   * displayed in this zone (defaults to the browser's, see App/lib/tz). */
  timezone: string;
  /** When set, the composer opens in EDIT mode hydrated from this scheduled
   * post's draft; saving reschedules it (see the hydrate effect and `publish`). */
  editPost?: ScheduledPost | null;
  /** Re-post a failed post: hydrate the composer with the original draft as a
   * FRESH post (not an edit/reschedule), with only the given platforms selected
   * (typically the ones that failed). `nonce` changes per click so the same
   * record can be re-posted again. */
  repostFrom?: { draft: PostDraft; platforms: PlatformId[]; nonce: number } | null;
  /** Leave EDIT mode: the parent clears its editing/re-post target so the
   * composer returns to a blank new post. Called by the "Start over" button. */
  onExitEdit?: () => void;
  /** Everything already on the workspace calendar — drives the schedule strip's
   * busy markers, clash warning, and batch auto-spacing so new posts don't pile
   * onto existing ones. */
  scheduled?: ScheduledPost[];
  /** The active workspace id. Drafts and snippets are workspace-scoped, so this
   * drives the reload effect below — switching brands reloads both surfaces. */
  workspaceId?: string | null;
  onDispatched: () => void;
  /** Live credit balance (each publish spends a platform-weighted amount — see
   * lib/credits), or null when billing isn't configured — in which case credit
   * gating is skipped. */
  creditsBalance?: number | null;
  /** Open the Billing surface so the user can top up when credits are short. */
  onBuyCredits?: () => void;
  /** Report the idempotency key of a backgrounded publish to the parent, which
   * owns the durable poll/refresh so it survives this panel being closed (#1). */
  onPublishInFlight?: (key: string) => void;
  /** Jump to the Activity log — offered from the batch-complete popup so the
   * creator can inspect (and get a fix for) anything that failed. */
  onViewActivity?: () => void;
  /** Jump to the calendar — offered from the single-post scheduled confirmation
   * so the creator can see the post in place. */
  onViewCalendar?: () => void;
  /** Leave the compose page while an immediate publish keeps running in the
   * background — "Hide — keep publishing" on the live overlay. Navigating away
   * lets the parent's durable tracker announce the outcome with a toast. */
  onHide?: () => void;
  /** Open the Connections page — shown when nothing is connected yet, so the
   * composer isn't a dead-end (every platform disabled) for a brand-new user. */
  onConnectAccounts?: () => void;
}) {
  const connected = useMemo(() => new Set(accounts.map((a) => a.platform)), [accounts]);

  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [tags, setTags] = useState("");
  const [mediaUrl, setMediaUrl] = useState("");
  const [mediaKind, setMediaKind] = useState<MediaKind>("video");
  // The content type of the current media, when known (set by the upload flow).
  // Lets the Instagram JPEG-only gate below tell a PNG/WebP/GIF from a JPEG
  // without re-reading the file. Null for a hand-pasted URL, where the gate
  // falls back to the URL's extension.
  const [mediaContentType, setMediaContentType] = useState<string | null>(null);

  // --- Direct file upload (browser → presigned PUT → R2) ---
  const [uploadName, setUploadName] = useState<string | null>(null);
  // The picked video itself, kept so the composer can preview it locally (TikTok
  // §5a) without waiting for — or depending on — the R2 round-trip.
  const [uploadVideo, setUploadVideo] = useState<File | null>(null);
  const [uploadPct, setUploadPct] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadDone, setUploadDone] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [visibility, setVisibility] = useState<Visibility>("private");
  const [selected, setSelected] = useState<Set<PlatformId>>(new Set());
  const [scheduleAt, setScheduleAt] = useState("");
  const [busy, setBusy] = useState(false);
  // One idempotency key per DRAFT: reused across every send of the same draft
  // (so a double-click, a network retry, or a press after a partial publish
  // can't double-post or double-charge) and replaced only once the creator
  // edits the draft into a different post. This backs up the button-disable —
  // it's the real guard against duplicate submits reaching the server.
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  // Set once this key has been handed to the server; see the effect below.
  const keyPinned = useRef(false);
  // Live inline dispatch panel for an IMMEDIATE publish. While `publishing`, each
  // target platform shows a spinner; once the background row lands (or the retry
  // money-guard returns results synchronously) it flips to `done` with the real
  // per-platform outcomes. `timeout` is the gentle fallback if the row never
  // arrives within the poll window. The schedule path never uses this — it keeps
  // its own `notice`.
  const [dispatchPanel, setDispatchPanel] = useState<{
    platforms: PlatformId[];
    phase: "publishing" | "done" | "timeout";
    results: DispatchResult[] | null;
  } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // --- Folder import (publish-queue ingest; see docs/FOLDER-IMPORT.md) ---
  const folderInputRef = useRef<HTMLInputElement | null>(null);
  const [imported, setImported] = useState<ImportedPost[]>([]);
  const [importRoot, setImportRoot] = useState("");
  const [importSkipped, setImportSkipped] = useState<string[]>([]);
  const [importError, setImportError] = useState<string | null>(null);
  const [loadedFolder, setLoadedFolder] = useState<string | null>(null);

  // --- Smart batch scheduler (shown only when >1 post is imported) ---
  // folder → datetime-local value; folder → publish-now flag; folder → status.
  const [batchTimes, setBatchTimes] = useState<Record<string, string>>({});
  const [batchPerDay, setBatchPerDay] = useState<PerDay>(DEFAULT_PER_DAY);
  // The editable time(s) the cadence fills posts in at ("HH:MM"), one per slot —
  // seeded from the cadence default, changeable by the creator. Each row's exact
  // time stays individually editable below; this just sets what the auto-fill uses.
  const [batchSlotTimes, setBatchSlotTimes] = useState<string[]>(defaultSlotTimes(DEFAULT_PER_DAY));
  const [batchPublishNow, setBatchPublishNow] = useState<Record<string, boolean>>({});
  const [batchStatus, setBatchStatus] = useState<Record<string, BatchRowStatus>>({});
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchSummary, setBatchSummary] = useState<string | null>(null);
  const [batchFailed, setBatchFailed] = useState(false);
  // Set when a whole batch run finishes — drives the completion popup that
  // confirms what went out and points at the Activity log for anything that
  // didn't. Dismissing it is cosmetic: it never touches the calendar or the log.
  const [batchComplete, setBatchComplete] = useState<{ ok: number; failed: number } | null>(null);
  // Confirmation popup for a SINGLE scheduled post — the batch gets one, so the
  // single flow should too (an inline line was too easy to miss). Publish-now
  // keeps its richer inline "Publishing…" panel instead.
  const [scheduleDone, setScheduleDone] = useState<{
    when: string;
    platforms: number;
    rescheduled: boolean;
  } | null>(null);
  // Identifies THIS import run. Every post's idempotency key is derived from it,
  // so pressing the button again re-sends the same keys (a real retry) while a
  // genuine re-import of the same folders gets fresh ones.
  const [importRunId, setImportRunId] = useState("");
  // ONE explicit set of TikTok choices for the whole import (see TikTokPanel).
  const [batchTt, setBatchTt] = useState<TikTokSettings>(EMPTY_TIKTOK_SETTINGS);
  // …and ONE explicit YouTube audience designation for the whole import.
  const [batchYt, setBatchYt] = useState<YouTubeSettings>(EMPTY_YOUTUBE_SETTINGS);
  // …and ONE explicit visibility, applied to every post that doesn't set its own
  // `visibility:` in post.yaml. No default: see VisibilityPanel.
  const [batchVisibility, setBatchVisibility] = useState<VisibilityChoice>(EMPTY_VISIBILITY);
  // folder → editable TikTok caption; folder → measured video duration (seconds).
  const [batchCaptions, setBatchCaptions] = useState<Record<string, string>>({});
  const [batchDurations, setBatchDurations] = useState<Record<string, number>>({});

  // --- Site-driven batch choices (item A: no-YAML batch upload) ---
  // Batch-wide default platforms, applied to any post that neither declares
  // platforms in its own post.yaml nor carries a per-row override — so a folder
  // of bare videos still publishes somewhere. Seeded to the connected set on
  // import (see initBatch).
  const [batchPlatforms, setBatchPlatforms] = useState<PlatformId[]>([]);
  // folder → per-post platform override chosen with the site pills. Absent = use
  // the post's post.yaml platforms, then the batch-wide default above.
  const [batchPlatformOverride, setBatchPlatformOverride] = useState<
    Record<string, PlatformId[]>
  >({});
  // folder → per-platform edited caption for the non-TikTok platforms (TikTok
  // keeps its own `batchCaptions` box). An empty entry falls back to the sidecar.
  const [batchText, setBatchText] = useState<Record<string, Partial<Record<PlatformId, string>>>>(
    {},
  );
  // folder → one-line idea that AI caption generation expands into a per-platform
  // caption; folder → mid-generation flag; a shared error line.
  const [batchIdea, setBatchIdea] = useState<Record<string, string>>({});
  const [batchAiBusy, setBatchAiBusy] = useState<Record<string, boolean>>({});
  // Per-folder so two rows generating at once can't clobber each other's message.
  const [batchAiError, setBatchAiError] = useState<Record<string, string>>({});

  // <input webkitdirectory> isn't in React's typed attributes; set it on the node.
  useEffect(() => {
    if (folderInputRef.current) folderInputRef.current.setAttribute("webkitdirectory", "");
  }, []);

  // Pending History-refresh timers for backgrounded publishes; cleared on unmount.
  const refreshTimers = useRef<number[]>([]);
  // The single active dispatch-status poll interval, or null. Held in a ref so it
  // survives re-renders and can be torn down on unmount or when a new dispatch
  // starts — the interval id also doubles as the "is this poll still current?"
  // token inside the async tick, guarding against setState after unmount.
  const dispatchPoll = useRef<number | null>(null);
  useEffect(() => {
    return () => {
      refreshTimers.current.forEach((t) => window.clearTimeout(t));
      if (dispatchPoll.current !== null) window.clearInterval(dispatchPoll.current);
    };
  }, []);

  // Stop the dispatch-status poll, if one is running.
  function stopDispatchPoll() {
    if (dispatchPoll.current !== null) {
      window.clearInterval(dispatchPoll.current);
      dispatchPoll.current = null;
    }
  }

  // Poll the status endpoint for THIS dispatch's key until the background
  // publisher records its one all-platforms row, then reveal the real results
  // inline. `key` is captured here (not read from the live `idempotencyKey`,
  // which rotates on the next draft edit) so the lookup always matches the
  // dispatch that opened the panel. Capped at ~120s: a stuck publish degrades to
  // a gentle "still publishing" note rather than spinning forever.
  function startDispatchPoll(key: string) {
    stopDispatchPoll();
    const startedAt = Date.now();
    // Declared before `tick` so the staleness guard can compare against it, and
    // assigned below before the immediate first invocation.
    let id: number;
    const tick = async () => {
      // Superseded by a newer dispatch, or unmounted → this tick is stale.
      if (dispatchPoll.current !== id) return;
      try {
        const { record } = await api.dispatchStatus(key);
        if (dispatchPoll.current !== id) return;
        // Only a FINALIZED row (real per-platform results) means done. The
        // money-guard's claim placeholder has empty results while the publish is
        // still running; resolving on it would flip the panel to "done" with no
        // outcomes — every row stuck spinning forever. The server already filters
        // these out, but guarding here too means a placeholder can never freeze
        // the panel regardless of what the endpoint returns.
        if (record && record.results.length > 0) {
          stopDispatchPoll();
          setDispatchPanel((prev) =>
            prev ? { ...prev, phase: "done", results: record.results } : prev,
          );
          // The row exists now, so the calendar/Recent can show it too.
          onDispatched();
          return;
        }
      } catch {
        // A transient status error is not fatal — keep polling until the cap.
        if (dispatchPoll.current !== id) return;
      }
      if (Date.now() - startedAt >= 120000) {
        stopDispatchPoll();
        setDispatchPanel((prev) => (prev ? { ...prev, phase: "timeout" } : prev));
        // The panel gave up polling, but the post may well have landed — pull
        // History once more so its result shows on the calendar/Recent even
        // though this inline panel timed out.
        onDispatched();
      }
    };
    id = window.setInterval(tick, 3000);
    dispatchPoll.current = id;
    // Fire once now so an already-recorded dispatch (or the money-guard retry)
    // surfaces without waiting a full interval.
    void tick();
  }

  // A backgrounded publish records its outcome to History as each platform
  // confirms. A multi-platform VIDEO dispatch can take ~60-90s (every platform
  // transcodes), so poll across that whole window — the old 3s/8s pair stopped
  // long before the post landed, forcing a manual refresh. Each tick re-fetches,
  // so a post appears leg-by-leg as it publishes.
  function scheduleHistoryRefreshes() {
    refreshTimers.current.forEach((t) => window.clearTimeout(t));
    refreshTimers.current = [3000, 8000, 15000, 25000, 40000, 60000, 90000].map((ms) =>
      window.setTimeout(() => onDispatched(), ms),
    );
  }

  // --- Per-platform caption overrides (populated by AI tailoring OR folder import) ---
  const [aiEdits, setAiEdits] = useState<Partial<Record<PlatformId, AiEdit>>>({});
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  // Where the current per-platform overrides came from, for an honest label.
  const [editsSource, setEditsSource] = useState<"ai" | "import" | null>(null);
  // Platforms an imported post has text for but that aren't connected, so we can
  // say so instead of silently dropping them (e.g. IG/FB before Meta approval).
  const [importMissing, setImportMissing] = useState<PlatformId[]>([]);

  // --- TikTok Direct-Post state (audit-required UX) ---
  const [tiktokInfo, setTiktokInfo] = useState<TikTokCreatorInfo | null>(null);
  const [tiktokLoading, setTiktokLoading] = useState(false);
  const [tiktokError, setTiktokError] = useState<string | null>(null);
  // Bumped by "Retry" to re-run the creator_info fetch after a transient failure.
  const [tiktokReload, setTiktokReload] = useState(0);
  // Privacy starts unchosen and the interaction toggles start OFF: TikTok's
  // Content Sharing Guidelines require that "Users must manually turn on these
  // interaction settings and none should be checked by default." (Direct Post
  // API — Required UX Implementation, §2b/§2c). The creator opts in; we never
  // opt in on their behalf.
  const [ttSettings, setTtSettings] = useState<TikTokSettings>(EMPTY_TIKTOK_SETTINGS);
  // Duration of the video currently in the composer, once its metadata loads.
  const [mediaDurationSec, setMediaDurationSec] = useState<number | null>(null);

  // --- YouTube upload state (ToS §9.1 / API Services Developer Policies) ---
  // The audience designation starts UNSET: ToS §9.1(iv) makes "Made for Kids"
  // the user's own declaration, so there is nothing legitimate to default it to.
  const [ytSettings, setYtSettings] = useState<YouTubeSettings>(EMPTY_YOUTUBE_SETTINGS);

  // --- Edit mode ---
  // The id of the scheduled post being edited, or null for a fresh compose. Set
  // by the hydrate effect below and consulted by `publish` to unschedule the old
  // row once the replacement is created (a reschedule).
  const [editId, setEditId] = useState<string | null>(null);
  const editing = editId !== null;

  // Future posts already on the calendar, feeding the schedule strip's busy
  // markers + clash warning and the batch auto-spacing. The post being edited is
  // excluded so a reschedule doesn't flag itself as a clash.
  const busyPosts: BusyPost[] = useMemo(
    () =>
      (scheduled ?? [])
        .filter((p) => p.at >= Date.now() && p.id !== editId)
        .map((p) => ({ at: p.at, platforms: p.platforms })),
    [scheduled, editId],
  );

  // Day-level schedule collisions for the batch, measured against the chosen
  // cadence: a day only "conflicts" once it holds MORE posts than the selected
  // posts-per-day (existing calendar posts + earlier batch rows both count). So
  // at 1/day a second post on a day is flagged; at 2/day only the third is — the
  // cadence is what the creator asked for, not a collision. A flagged row gets
  // the next day with spare capacity to move to. Soft warning (ignorable);
  // "publish now" rows carry no date and are exempt.
  const batchConflicts = useMemo(() => {
    const dayKey = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
        d.getDate(),
      ).padStart(2, "0")}`;
    const capacity = batchPerDay; // 1 or 2 posts per day
    const count = new Map<string, number>();
    for (const b of busyPosts) {
      const k = dayKey(new Date(b.at));
      count.set(k, (count.get(k) ?? 0) + 1);
    }
    const out = new Map<string, { suggested: string }>();
    for (const post of imported) {
      if (batchPublishNow[post.folder]) continue;
      const local = batchTimes[post.folder];
      if (!local || local.length < 10) continue;
      const key = local.slice(0, 10); // datetime-local is always "YYYY-MM-DD…"
      if ((count.get(key) ?? 0) >= capacity) {
        // This day is already at capacity — walk forward to the next day that
        // still has room, keeping the same clock time.
        const d = new Date(local);
        let guard = 0;
        do {
          d.setDate(d.getDate() + 1);
          guard += 1;
        } while ((count.get(dayKey(d)) ?? 0) >= capacity && guard < 400);
        const k = dayKey(d);
        count.set(k, (count.get(k) ?? 0) + 1);
        out.set(post.folder, { suggested: toInputValue(d) });
      } else {
        count.set(key, (count.get(key) ?? 0) + 1);
      }
    }
    return out;
  }, [imported, batchTimes, batchPublishNow, busyPosts, batchPerDay]);

  // --- Saved drafts & snippets (workspace-scoped) ---
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [snippets, setSnippets] = useState<Snippet[]>([]);
  // The id of a saved draft currently loaded into the composer, if any. Saving
  // updates it (carries the id); scheduling/dispatching it deletes it — it has
  // become a real post. Null for a fresh compose.
  const [loadedDraftId, setLoadedDraftId] = useState<string | null>(null);
  const [savingDraft, setSavingDraft] = useState(false);

  const captionSnippets = useMemo(() => snippets.filter((s) => s.kind === "caption"), [snippets]);
  const hashtagSnippets = useMemo(() => snippets.filter((s) => s.kind === "hashtags"), [snippets]);

  // Best-effort reloads after a mutation — a failure just leaves the last list.
  async function reloadDrafts() {
    try {
      setDrafts(await api.drafts());
    } catch {
      /* keep the current list */
    }
  }
  async function reloadSnippets() {
    try {
      setSnippets(await api.snippets());
    } catch {
      /* keep the current list */
    }
  }

  // Load both surfaces for the active workspace, and reload when it changes so
  // switching brands never shows another brand's drafts/snippets. Best-effort:
  // a failure simply leaves the surfaces empty (they're an aid, not the flow).
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [d, s] = await Promise.all([api.drafts(), api.snippets()]);
        if (!cancelled) {
          setDrafts(d);
          setSnippets(s);
        }
      } catch {
        /* best effort */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  // Everything that decides WHAT this post is. Changing any of it makes the next
  // send a different submission, which must not be deduped against the last one
  // — so that, and only that, mints a new idempotency key. Sending does NOT:
  // `api.dispatch` answering `{ queued: true }` means the publish was ENQUEUED,
  // not that it finished, and rotating the key there (as this used to) made a
  // second press a submission the server couldn't recognise, re-posting the
  // platforms that had already gone out.
  const draftSignature = JSON.stringify([
    title,
    body,
    tags,
    mediaUrl,
    mediaKind,
    visibility,
    scheduleAt,
    [...selected].sort(),
    aiEdits,
    ttSettings,
    ytSettings,
  ]);
  useEffect(() => {
    // No-op until something has actually been sent under the current key.
    if (!keyPinned.current) return;
    keyPinned.current = false;
    setIdempotencyKey(crypto.randomUUID());
  }, [draftSignature]);

  // Hydrate the composer's fields from a PostDraft + its platform selection.
  // Shared by the scheduled-post editor and the saved-draft loader, so a
  // reopened draft and a reopened scheduled post fill the form identically — the
  // mappings here are the exact reverse of `buildDraft`. Deliberately does NOT
  // touch the schedule time or the edit/draft ids; the callers own those.
  function hydrateComposerFrom(draft: PostDraft, platforms: PlatformId[]) {
    setTitle(draft.title);
    setBody(draft.body);
    setTags(draft.tags.join(", "));
    setVisibility(draft.visibility);
    // Reuse the already-uploaded media URL — reopening never forces a re-upload.
    setMediaUrl(draft.media?.url ?? "");
    setMediaKind(draft.media?.kind ?? "video");
    setMediaContentType(draft.media?.mimeType ?? null);
    // Drop any stale local upload state so the reused URL is the source of truth.
    setUploadName(null);
    setUploadVideo(null);
    setUploadDone(false);
    setUploadPct(0);
    setUploadError(null);
    setSelected(new Set(platforms));
    setTtSettings(
      draft.tiktok ? tiktokSettingsFromOptions(draft.tiktok) : EMPTY_TIKTOK_SETTINGS,
    );
    setYtSettings(
      draft.youtube
        ? { madeForKids: draft.youtube.madeForKids ? "yes" : "no" }
        : EMPTY_YOUTUBE_SETTINGS,
    );
    // Per-platform overrides feed the same review channel AI/import edits use.
    const edits: Partial<Record<PlatformId, AiEdit>> = {};
    for (const [platform, ov] of Object.entries(draft.perPlatform ?? {})) {
      if (!ov || !isPlatformId(platform)) continue;
      edits[platform] = {
        title: ov.title ?? "",
        body: ov.body ?? "",
        tags: (ov.tags ?? []).join(", "),
      };
    }
    setAiEdits(edits);
    // Neither "ai" nor "import" is literally the origin; "ai" gives the review
    // rows an honest "edit before posting" affordance without a new source label.
    setEditsSource(Object.keys(edits).length > 0 ? "ai" : null);
  }

  // Clear the composer back to a blank post after a publish, so the creator is
  // never left staring at content that already went out (and can't accidentally
  // re-send it). Platform selection is KEPT — the common next move is posting the
  // next clip to the same set. Content, media, per-platform edits, schedule, and
  // any edit/draft linkage are cleared, and the idempotency key is rotated so the
  // next send is its own submission rather than a dedupe of the one just sent.
  function resetComposer() {
    setTitle("");
    setBody("");
    setTags("");
    setVisibility("private");
    setMediaUrl("");
    setMediaKind("video");
    setMediaContentType(null);
    setUploadName(null);
    setUploadVideo(null);
    setUploadDone(false);
    setUploadPct(0);
    setUploadError(null);
    setTtSettings(EMPTY_TIKTOK_SETTINGS);
    setYtSettings(EMPTY_YOUTUBE_SETTINGS);
    setAiEdits({});
    setEditsSource(null);
    setScheduleAt("");
    setEditId(null);
    setLoadedDraftId(null);
    setError(null);
    setNotice(null);
    stopDispatchPoll();
    setDispatchPanel(null);
    // Rotate the key now (not just via the draft-signature effect) so the next
    // compose can never be deduped against the send that just completed.
    keyPinned.current = false;
    setIdempotencyKey(crypto.randomUUID());
  }

  // Load a saved draft into the composer. Same hydration the scheduled-post
  // editor uses, but as a DRAFT: no scheduled time and no edit id — just the
  // draft id it carries, so a subsequent save updates it in place and a
  // schedule/dispatch deletes it (it's now a real post).
  function loadDraft(d: Draft) {
    hydrateComposerFrom(d.draft, d.platforms);
    setEditId(null);
    setScheduleAt("");
    setLoadedDraftId(d.id);
    stopDispatchPoll();
    setDispatchPanel(null);
    setError(null);
    setNotice(`Loaded draft “${draftLabel(d)}”.`);
  }

  // Hydrate every composer field from a scheduled post when the parent hands one
  // in to edit. Keyed on the id so opening a DIFFERENT post re-hydrates while
  // ordinary re-renders don't clobber in-progress edits. When it clears (the
  // slide-over closed), edit mode ends so the next fresh compose isn't stuck on
  // "Save changes".
  useEffect(() => {
    if (!editPost) {
      // The parent cleared the edit target (e.g. "Create post" or "Start over").
      // If we WERE editing, blank the form so we don't linger on the edited
      // post's content in a now-"new post" mode; on a fresh mount (never edited)
      // just ensure edit mode is off without wiping an in-progress new compose.
      if (editId !== null) resetComposer();
      else setEditId(null);
      return;
    }
    hydrateComposerFrom(editPost.draft, editPost.platforms);
    // Editing a scheduled post is not editing a saved draft.
    setLoadedDraftId(null);
    // Epoch ms → LOCAL datetime-local, matching the calendar's local-time contract.
    setScheduleAt(epochToInputValue(editPost.at, timezone));
    setEditId(editPost.id);
    // Clear leftover feedback from any previous compose in this mounted instance.
    stopDispatchPoll();
    setDispatchPanel(null);
    setNotice(null);
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editPost?.id]);

  // Re-post: hydrate from a failed post's draft as a brand-NEW compose (no editId,
  // so `publish` creates a fresh post rather than rescheduling), with only the
  // requested platforms selected. Keyed on `nonce` so clicking Re-post again
  // re-hydrates. Publish-now by default (schedule cleared) — the creator can set
  // a time if they'd rather.
  useEffect(() => {
    if (!repostFrom) return;
    hydrateComposerFrom(repostFrom.draft, repostFrom.platforms);
    setEditId(null);
    setLoadedDraftId(null);
    setScheduleAt("");
    stopDispatchPoll();
    setDispatchPanel(null);
    setNotice(null);
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repostFrom?.nonce]);

  // Preview the local file when we have one (instant, and it survives an upload
  // failure); otherwise fall back to whatever video URL is in the field.
  const previewFile = uploadVideo;
  const previewSrc = !uploadVideo && mediaKind === "video" ? mediaUrl : "";
  // A new source invalidates the duration measured from the previous one.
  useEffect(() => setMediaDurationSec(null), [previewFile, previewSrc]);

  const tiktokSelected = selected.has("tiktok");
  // §1c's limit, resolved once for every preview and every gate below. `null`
  // means "this account has no usable limit", so nothing is ever measured
  // against it — see `tiktokMaxDurationSec` for why that is not the same as 0.
  const tiktokMaxSec = tiktokMaxDurationSec(tiktokInfo);

  const youtubeSelected = selected.has("youtube");
  // Developer Policies III.E.3.f — the channel this upload will land on, named
  // on the screen that authorises it (not just buried in the Accounts list).
  const youtubeChannel = useMemo(
    () => accounts.find((a) => a.platform === "youtube")?.displayName ?? "",
    [accounts],
  );
  // The text YouTube would ACTUALLY receive: a per-platform override (AI-tailored
  // or imported) wins over the shared draft, exactly as `buildPerPlatform` sends
  // it. Measuring the shared fields instead would count the wrong string.
  const youtubeText: YouTubeDraftText = useMemo(() => {
    const edit = aiEdits.youtube;
    return {
      title: edit?.title ?? title,
      body: edit?.body ?? body,
      tags: (edit?.tags ?? tags)
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
    };
  }, [aiEdits, title, body, tags]);

  // Batch mode kicks in when more than one post is imported.
  const batchMode = imported.length > 1;

  // The batch-wide default platform set, narrowed to connected accounts (a pill
  // for a disconnected platform can't actually send). Item A.
  const batchDefaultPlatforms = useMemo(
    () => batchPlatforms.filter((id) => connected.has(id)),
    [batchPlatforms, connected],
  );

  // The platforms ONE imported post targets, resolving the three sources in
  // priority order: an explicit per-row override wins; otherwise the post.yaml's
  // own `platforms:`; otherwise the batch-wide default. This single seam is what
  // every batch derivation and `scheduleAllBatch` key off, so a bare video
  // folder (empty `data`) still resolves to real targets. Connection filtering
  // stays where it always was — in `scheduleAllBatch` — so this returns intent,
  // not the final send list.
  const effectivePlatforms = useCallback(
    (post: ImportedPost): PlatformId[] => {
      const override = batchPlatformOverride[post.folder];
      if (override) return override;
      const declared = platformsInPost(post.data);
      if (declared.length > 0) return declared;
      return batchDefaultPlatforms;
    },
    [batchPlatformOverride, batchDefaultPlatforms],
  );
  // What the imported post.yaml files ASK for vs what the batch can actually
  // post to. `scheduleAllBatch` filters its targets by `connected`, so a queue
  // that declares `tiktok:` while TikTok is not a connected account never sends
  // anything to TikTok — and must therefore never be gated by TikTok's rules.
  // Conditioning the gate on `connected` too is what keeps such a queue
  // schedulable (and stops us fetching creator_info for an account we don't have).
  const batchDeclaresTiktok = useMemo(
    () => imported.some((p) => effectivePlatforms(p).includes("tiktok")),
    [imported, effectivePlatforms],
  );
  const batchHasTiktok = batchDeclaresTiktok && connected.has("tiktok");

  // The same question for YouTube: an import only has to satisfy YouTube's
  // upload rules when YouTube is genuinely one of its targets.
  const batchDeclaresYoutube = useMemo(
    () => imported.some((p) => effectivePlatforms(p).includes("youtube")),
    [imported, effectivePlatforms],
  );
  const batchHasYoutube = batchDeclaresYoutube && connected.has("youtube");
  // Unlike TikTok's, this gate has no third-party call behind it — nothing can
  // fail to load, so there is nothing to degrade past: one explicit designation
  // covers the whole import (ToS §9.1(iv)).
  const batchYoutubeBlocker: string | null = batchHasYoutube
    ? youtubeSettingsBlocker(batchYt, "batch")
    : null;

  // Visibility reaches only the platforms that read it — YouTube and TikTok — so
  // the question is asked on exactly the same terms as theirs: when one of them
  // is genuinely a target of this import. A batch that lands only on Instagram,
  // Facebook or X publishes publicly regardless (see VisibilityNote), and being
  // asked to choose a privacy status nothing would honour would be theatre.
  //
  // When it IS asked, it is asked properly: no preselected value, and no
  // scheduling until the creator answers. Defaulting it is precisely how an
  // entire imported queue used to go up private on YouTube without a word.
  const batchHonoursVisibility = batchHasYoutube || batchHasTiktok;
  const batchVisibilityBlocker: string | null = batchHonoursVisibility
    ? visibilityBlocker(batchVisibility, "batch")
    : null;

  // Latest time in the batch, read back off the values the rows actually hold
  // (suggested or hand-edited) so the end-date summary can't drift from them.
  const batchLastTime = useMemo(() => {
    const stamps = imported
      .map((p) => new Date(batchTimes[p.folder] ?? "").getTime())
      .filter((ms) => !Number.isNaN(ms));
    return stamps.length > 0 ? new Date(Math.max(...stamps)) : null;
  }, [imported, batchTimes]);

  // A stable id for the connected TikTok account. Creator info is per-account,
  // so it must be refetched — not left stale — if the account changes underneath
  // the composer (disconnect + reconnect a different handle).
  const tiktokAccountId = useMemo(
    () => accounts.find((a) => a.platform === "tiktok")?.accountId ?? null,
    [accounts],
  );
  // Drop cached creator info the moment the connected TikTok account changes, so
  // the fetch effect below re-runs against the new account rather than serving
  // the previous account's nickname/limits/privacy options.
  useEffect(() => {
    setTiktokInfo(null);
    setTiktokError(null);
  }, [tiktokAccountId]);

  // Fetch the creator's account info the first time TikTok is selected OR a
  // batch targeting a CONNECTED TikTok account is imported — the options below
  // MUST be populated from this response (TikTok audit rule).
  useEffect(() => {
    // Only depend on selection + whether we already have the info. Do NOT depend
    // on `tiktokLoading`: we flip it to true inside this effect, and if it were a
    // dependency that state change would re-run the effect, whose cleanup sets
    // `cancelled = true` — so the in-flight request's result gets discarded and
    // the panel stays stuck on "Loading" forever.
    if ((!tiktokSelected && !batchHasTiktok) || tiktokInfo) return;
    let cancelled = false;
    setTiktokLoading(true);
    setTiktokError(null);
    api
      .tiktokCreatorInfo()
      .then((info) => {
        if (!cancelled) setTiktokInfo(info);
      })
      .catch((err) => {
        if (!cancelled) {
          setTiktokError(err instanceof Error ? err.message : "Failed to load TikTok account.");
        }
      })
      // Deliberately NOT guarded by `cancelled`: settling the request always
      // ends the loading state. Storing the info re-runs this effect (it is a
      // dependency), and that cleanup can land before `finally` does — guarding
      // here would leave the panel reading "Loading" forever with the publish
      // button disabled. Clearing a stale loading flag is always correct.
      .finally(() => setTiktokLoading(false));
    return () => {
      cancelled = true;
    };
  }, [tiktokSelected, batchHasTiktok, tiktokInfo, tiktokReload, tiktokAccountId]);

  // Re-run the fetch above after a transient failure, without re-importing.
  function retryTiktokInfo() {
    setTiktokError(null);
    setTiktokReload((n) => n + 1);
  }

  // NOTE: the batch privacy level is deliberately NOT seeded from
  // `privacyLevelOptions` when the creator info arrives. TikTok requires that
  // "Users must manually select the privacy status from a dropdown and there
  // should be no default value." — same rule the single-post picker follows.

  function toggle(id: PlatformId) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  async function handleFile(file: File) {
    // Re-entrancy guard: a second pick/drop while an upload is in flight would
    // race two presign+PUT flows and leave mediaUrl pointing at whichever landed
    // last. One upload at a time.
    if (uploading) return;
    setUploadError(null);
    if (!ALLOWED_CONTENT_TYPES.has(file.type)) {
      setUploadError(
        "Unsupported file type. Allowed: MP4, MOV, WebM video; JPEG, PNG, WebP, GIF images.",
      );
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setUploadError("File is too large (2 GB maximum).");
      return;
    }
    setUploading(true);
    setUploadPct(0);
    setUploadDone(false);
    setUploadName(file.name);
    // Preview straight away — the creator sees the clip while it uploads.
    setUploadVideo(file.type.startsWith("video/") ? file : null);
    try {
      const presigned = await api.presignUpload({
        filename: file.name,
        contentType: file.type,
        size: file.size,
      });
      if (!presigned.configured || !presigned.uploadUrl || !presigned.publicUrl) {
        throw new Error("File upload isn't set up yet.");
      }
      await putWithProgress(presigned.uploadUrl, file, presigned.requiredHeaders ?? {}, setUploadPct);
      setMediaUrl(presigned.publicUrl);
      setMediaKind(file.type.startsWith("image/") ? "image" : "video");
      setMediaContentType(file.type || null);
      setUploadDone(true);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : "Upload failed.");
      setUploadName(null);
      setUploadVideo(null);
    } finally {
      setUploading(false);
    }
  }

  // Clear the uploaded file and the media URL it populated.
  function clearUpload() {
    setMediaUrl("");
    setMediaKind("video");
    setMediaContentType(null);
    setUploadName(null);
    setUploadVideo(null);
    setUploadDone(false);
    setUploadPct(0);
    setUploadError(null);
  }

  // Read a picked publish-queue folder into a list of posts. If exactly one post
  // is found, load it straight into the composer.
  async function onFolderPicked(files: FileList | null) {
    setImportError(null);
    setImported([]);
    setImportSkipped([]);
    setLoadedFolder(null);
    resetBatch();
    if (!files || files.length === 0) return;
    try {
      const { root, posts, skipped } = await parseFolder(files);
      if (posts.length === 0) {
        setImportError(
          skipped.length > 0
            ? `${skipped.length} folder(s) were skipped — they have no video and no post details file (post.yaml). A folder with just a video is fine now; you'll choose its platforms and captions here.`
            : "No posts found. Pick a folder of videos, or one small folder per post inside it.",
        );
        return;
      }
      setImportRoot(root);
      setImported(posts);
      setImportSkipped(skipped);
      // One post → load into the composer (unchanged). More than one → prep the
      // smart batch panel with suggested times, nothing scheduled yet.
      if (posts.length === 1) await loadImportedPost(posts[0]!);
      else initBatch(posts);
    } catch (err) {
      setImportError(err instanceof Error ? err.message : "Couldn't read that folder.");
    }
  }

  // Wipe all batch scheduling state (run counts, statuses, chosen times).
  function resetBatch() {
    setBatchTimes({});
    setBatchPerDay(DEFAULT_PER_DAY);
    setBatchSlotTimes(defaultSlotTimes(DEFAULT_PER_DAY));
    setBatchPublishNow({});
    setBatchStatus({});
    setBatchRunning(false);
    setBatchSummary(null);
    setBatchFailed(false);
    setBatchComplete(null);
    setImportRunId("");
    setBatchTt(EMPTY_TIKTOK_SETTINGS);
    setBatchYt(EMPTY_YOUTUBE_SETTINGS);
    setBatchVisibility(EMPTY_VISIBILITY);
    setBatchCaptions({});
    setBatchDurations({});
    setBatchPlatforms([]);
    setBatchPlatformOverride({});
    setBatchText({});
    setBatchIdea({});
    setBatchAiBusy({});
    setBatchAiError({});
  }

  // Spread `posts` at the given cadence and store the result as editable
  // datetime-local values. Posts with an explicit `schedule:` keep their exact
  // time; only the suggested ones move.
  function spreadBatchTimes(
    posts: ImportedPost[],
    perDay: PerDay,
    slotTimes: string[] = batchSlotTimes,
  ) {
    const slotMinutes = slotTimes
      .map((t) => timeToMinutes(t))
      .filter((m): m is number => m !== null);
    const times = buildBatchTimes(
      posts.map((p) => ({ folder: p.folder, scheduleIso: p.data.schedule })),
      new Date(),
      perDay,
      busyPosts.map((b) => b.at),
      slotMinutes.length > 0 ? slotMinutes : undefined,
    );
    const values: Record<string, string> = {};
    for (const post of posts) {
      const when = times.get(post.folder);
      values[post.folder] = when ? toInputValue(when) : "";
    }
    setBatchTimes(values);
  }

  // Picking a different cadence re-spreads the suggestions straight away, so the
  // rows and the end-date summary always show what would actually be scheduled.
  // The cadence also resets the editable time(s) to that cadence's default.
  function chooseBatchPerDay(perDay: PerDay) {
    const slots = defaultSlotTimes(perDay);
    setBatchPerDay(perDay);
    setBatchSlotTimes(slots);
    spreadBatchTimes(imported, perDay, slots);
  }

  // Edit one of the cadence's fill-in times and re-spread immediately, so the
  // rows below update to the chosen time. Each row stays individually editable.
  function chooseBatchSlotTime(index: number, value: string) {
    const next = batchSlotTimes.slice();
    next[index] = value;
    setBatchSlotTimes(next);
    spreadBatchTimes(imported, batchPerDay, next);
  }

  // --- Site-driven batch platform + caption editing (item A) ---

  // Toggle one platform in the batch-wide default set.
  function toggleBatchPlatform(id: PlatformId) {
    setBatchPlatforms((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }

  // Toggle one platform for a single row. The first toggle seeds the override
  // from the row's CURRENT effective platforms, so flipping one pill never
  // silently drops the others the row was already going to.
  function toggleRowPlatform(post: ImportedPost, id: PlatformId) {
    setBatchPlatformOverride((prev) => {
      const current = prev[post.folder] ?? effectivePlatforms(post);
      const next = current.includes(id)
        ? current.filter((x) => x !== id)
        : [...current, id];
      return { ...prev, [post.folder]: next };
    });
  }

  // Drop a row's override so it follows the batch-wide default / its sidecar.
  function clearRowOverride(folder: string) {
    setBatchPlatformOverride((prev) => {
      if (prev[folder] == null) return prev;
      const next = { ...prev };
      delete next[folder];
      return next;
    });
  }

  // Set one platform's caption box for a row.
  function setRowText(folder: string, id: PlatformId, value: string) {
    setBatchText((prev) => ({ ...prev, [folder]: { ...(prev[folder] ?? {}), [id]: value } }));
  }

  // AI-generate a tailored caption per platform for one row, expanding its idea
  // line. TikTok's result feeds its own caption box (`batchCaptions`); the rest
  // feed `batchText`. Each row generates independently.
  async function generateRowCaptions(post: ImportedPost) {
    const folder = post.folder;
    // Set or clear THIS row's AI error, leaving other rows' messages intact.
    const setRowAiError = (msg: string | null) =>
      setBatchAiError((prev) => {
        if (msg === null) {
          if (prev[folder] == null) return prev;
          const next = { ...prev };
          delete next[folder];
          return next;
        }
        return { ...prev, [folder]: msg };
      });
    const idea = (batchIdea[folder] ?? "").trim();
    if (!idea) {
      setRowAiError("Add a line about this post first, then generate.");
      return;
    }
    const platforms = effectivePlatforms(post).filter((id) => connected.has(id));
    if (platforms.length === 0) {
      setRowAiError("Pick at least one connected platform for this post first.");
      return;
    }
    setRowAiError(null);
    setBatchAiBusy((prev) => ({ ...prev, [folder]: true }));
    try {
      const res = await api.aiCaptions({ title: idea, body: idea, tags: [] }, platforms);
      if (!res.configured || !res.suggestions) throw new Error("AI captions aren't enabled.");
      const textNext: Partial<Record<PlatformId, string>> = { ...(batchText[folder] ?? {}) };
      let ttNext: string | null = null;
      for (const [platform, cap] of Object.entries(res.suggestions)) {
        if (!cap || !isPlatformId(platform)) continue;
        // TikTok publishes from `title`, the others from `body` (see _lib/ai.ts).
        const text = platform === "youtube" ? cap.title : cap.body || cap.title;
        if (platform === "tiktok") ttNext = text;
        else textNext[platform] = text;
      }
      setBatchText((prev) => ({ ...prev, [folder]: textNext }));
      if (ttNext !== null) setBatchCaptions((prev) => ({ ...prev, [folder]: ttNext! }));
    } catch (err) {
      setRowAiError(err instanceof Error ? err.message : "Couldn't generate captions.");
    } finally {
      setBatchAiBusy((prev) => ({ ...prev, [folder]: false }));
    }
  }

  // Pre-fill an editable suggested time for every post (explicit schedules kept,
  // the rest spread at the default cadence), all defaulting to "scheduled".
  function initBatch(posts: ImportedPost[]) {
    const slots = defaultSlotTimes(DEFAULT_PER_DAY);
    setBatchPerDay(DEFAULT_PER_DAY);
    setBatchSlotTimes(slots);
    spreadBatchTimes(posts, DEFAULT_PER_DAY, slots);
    setBatchPublishNow({});
    setBatchStatus({});
    setBatchSummary(null);
    setBatchFailed(false);
    // Minted ONCE per import, not per press: this is what makes a re-press an
    // idempotent retry rather than a second set of posts.
    setImportRunId(crypto.randomUUID());
    // Seed each row's editable TikTok caption from its sidecar; edits replace it.
    setBatchCaptions(
      Object.fromEntries(posts.map((post) => [post.folder, importedTiktokCaption(post)])),
    );
    // Seed the non-TikTok per-platform caption boxes from each sidecar too, so a
    // yaml import shows its text and a bare folder starts blank (item A).
    setBatchText(Object.fromEntries(posts.map((post) => [post.folder, seedBatchText(post)])));
    // Seed each row's AI "idea" from its richest existing caption, falling back to
    // a humanised folder name so even a bare folder has something to expand.
    setBatchIdea(Object.fromEntries(posts.map((post) => [post.folder, seedBatchIdea(post)])));
    // Batch-wide platforms default to every connected account — so a folder of
    // bare videos publishes everywhere until the creator narrows it.
    setBatchPlatforms(ALL_PLATFORM_IDS.filter((id) => connected.has(id)));
    setBatchPlatformOverride({});
    setBatchAiBusy({});
    setBatchAiError({});
    setBatchDurations({});
  }

  // Populate the composer from one imported post: base text, per-platform
  // overrides, platform selection, optional schedule, and the uploaded video.
  async function loadImportedPost(post: ImportedPost) {
    const p = post.data.platforms ?? {};
    const yt = p.youtube ?? {};
    const ig = p.instagram ?? {};
    const tt = p.tiktok ?? {};
    const x = p.x ?? {};
    const fb = p.facebook ?? {};

    // Base draft = the richest text available, used as the fallback for any
    // selected platform without its own override.
    setTitle(yt.title ?? tt.caption ?? ig.caption ?? x.text ?? fb.caption ?? "");
    setBody(yt.description ?? ig.caption ?? tt.caption ?? x.text ?? fb.caption ?? "");
    setTags("");

    // Per-platform overrides (fed to the same perPlatform channel as AI edits).
    // Put each platform's caption in the field its adapter actually reads, and
    // leave the other empty so the two review fields can't disagree about which
    // wins: TikTok reads title-first (title||body); X reads body-first (body||title).
    const edits: Partial<Record<PlatformId, AiEdit>> = {};
    if (p.youtube) edits.youtube = { title: yt.title ?? "", body: yt.description ?? "", tags: "" };
    if (p.instagram) edits.instagram = { title: "", body: ig.caption ?? "", tags: "" };
    if (p.tiktok) edits.tiktok = { title: tt.caption ?? "", body: "", tags: "" };
    if (p.x) edits.x = { title: "", body: x.text ?? "", tags: "" };
    if (p.facebook) edits.facebook = { title: "", body: fb.caption ?? "", tags: "" };
    setAiEdits(edits);
    setEditsSource("import");

    // Select the platforms the post targets that are actually connected, and
    // remember the ones it targets but that aren't connected — so we can tell the
    // creator they were skipped rather than dropping their text silently.
    const targets = platformsInPost(post.data);
    const sel = new Set<PlatformId>();
    for (const id of targets) if (connected.has(id)) sel.add(id);
    setSelected(sel);
    setImportMissing(targets.filter((id) => !connected.has(id)));

    // Optional schedule.
    const local = toDatetimeLocal(post.data.schedule);
    setScheduleAt(local ?? "");

    // Optional visibility — the same precedence the batch applies, on the
    // one-post path: the sidecar wins when it names a usable value, and the
    // creator sees the result in the picker before anything is sent.
    const wanted = postVisibility(post.data);
    if (wanted) setVisibility(wanted);

    setLoadedFolder(post.folder);

    // Upload the video last (async) — reuses the R2 presign flow and sets mediaUrl.
    clearUpload();
    if (post.mediaFile) await handleFile(post.mediaFile);
    else setImportError(`"${post.folder}" has no video to attach.`);
  }

  // Upload one file via the R2 presign flow and RETURN its public URL + kind,
  // without touching the single-composer upload state. Used by the batch loop.
  async function uploadMediaFile(file: File): Promise<{ url: string; kind: MediaKind }> {
    if (!ALLOWED_CONTENT_TYPES.has(file.type)) {
      throw new Error("Unsupported file type (MP4, MOV, WebM, JPEG, PNG, WebP, GIF).");
    }
    if (file.size > MAX_UPLOAD_BYTES) throw new Error("File is too large (2 GB maximum).");
    const presigned = await api.presignUpload({
      filename: file.name,
      contentType: file.type,
      size: file.size,
    });
    if (!presigned.configured || !presigned.uploadUrl || !presigned.publicUrl) {
      throw new Error("File upload isn't enabled.");
    }
    await putWithProgress(presigned.uploadUrl, file, presigned.requiredHeaders ?? {}, () => {});
    return { url: presigned.publicUrl, kind: file.type.startsWith("image/") ? "image" : "video" };
  }

  // The visibility one imported post publishes with. The post's own
  // `visibility:` wins; otherwise the creator's batch-wide choice applies. The
  // final fallback is reachable only for an import nothing honours visibility on
  // (no YouTube, no TikTok), where the gate deliberately never asks.
  function resolveVisibility(post: ImportedPost): Visibility {
    return postVisibility(post.data) ?? (batchVisibility || "private");
  }

  // Build the PostDraft for one imported post (mirrors loadImportedPost but pure),
  // scoped to the platforms it actually targets. `tiktokCaption` is whatever the
  // row's caption box currently holds — the creator's edit, not the sidecar.
  function buildImportedDraft(
    post: ImportedPost,
    targets: PlatformId[],
    media: { url: string; kind: MediaKind },
    tiktokOpts: TikTokOptions | null,
    tiktokCaption: string,
    youtubeOpts: YouTubeOptions | null,
    visibility: Visibility,
    /** Site-edited captions (item A), keyed by platform. A non-empty entry wins
     * over the sidecar; empty/absent falls back to it. TikTok is carried by
     * `tiktokCaption` instead (its own row box), so it isn't read from here. */
    textOverride: Partial<Record<PlatformId, string>> = {},
  ): PostDraft {
    const p = post.data.platforms ?? {};
    const yt = p.youtube ?? {};
    const ig = p.instagram ?? {};
    const x = p.x ?? {};
    const fb = p.facebook ?? {};
    // The row's box wins over the sidecar wherever both exist, so what's on
    // screen and what's published can never disagree; a bare folder (empty
    // `data`) resolves entirely to the typed/AI text.
    const pick = (id: PlatformId, sidecar: string): string => {
      const edited = textOverride[id];
      return edited != null && edited.trim() !== "" ? edited : sidecar;
    };
    const ytTitle = pick("youtube", yt.title ?? "");
    // YouTube needs a description too; a bare-folder post has none in the sidecar,
    // so fall back to the title rather than uploading an empty description.
    const ytDesc = yt.description ?? ytTitle;
    const igCap = pick("instagram", ig.caption ?? "");
    const xText = pick("x", x.text ?? "");
    const fbCap = pick("facebook", fb.caption ?? "");
    // TikTok caption is already resolved (row box → sidecar) by the caller.
    const ttCap = tiktokCaption;

    const perPlatform: Partial<Record<PlatformId, PerPlatformOverride>> = {};
    if (targets.includes("youtube"))
      perPlatform.youtube = { title: ytTitle, body: ytDesc, tags: [] };
    if (targets.includes("instagram"))
      perPlatform.instagram = { title: "", body: igCap, tags: [] };
    if (targets.includes("tiktok"))
      perPlatform.tiktok = { title: ttCap, body: "", tags: [] };
    if (targets.includes("x")) perPlatform.x = { title: "", body: xText, tags: [] };
    if (targets.includes("facebook"))
      perPlatform.facebook = { title: "", body: fbCap, tags: [] };

    // Shared fallback title/body, drawn only from platforms actually targeted so a
    // dropped platform's text can't leak into the shared draft.
    const has = (id: PlatformId) => targets.includes(id);
    return {
      title:
        (has("youtube") && ytTitle) ||
        (has("tiktok") && ttCap) ||
        (has("instagram") && igCap) ||
        (has("x") && xText) ||
        (has("facebook") && fbCap) ||
        "",
      body:
        (has("youtube") && ytDesc) ||
        (has("instagram") && igCap) ||
        (has("tiktok") && ttCap) ||
        (has("x") && xText) ||
        (has("facebook") && fbCap) ||
        "",
      tags: [],
      visibility,
      media: { kind: media.kind, url: media.url },
      ...(targets.includes("tiktok") && tiktokOpts ? { tiktok: tiktokOpts } : {}),
      ...(targets.includes("youtube") && youtubeOpts ? { youtube: youtubeOpts } : {}),
      ...(Object.keys(perPlatform).length > 0 ? { perPlatform } : {}),
    };
  }

  // TikTok's creator_info is a third-party call, and a third-party outage must
  // never be able to block publishing to unrelated platforms. So a failed fetch
  // DROPS TikTok from the batch (see `scheduleAllBatch`) instead of gating it —
  // the rest of the queue schedules as normal.
  const batchTiktokSkipped = batchHasTiktok && tiktokError !== null;
  // TikTok is a real target for this batch only while it is connected, declared,
  // and its creator info is loadable. Everything TikTok-specific hangs off this.
  const batchTiktokTargeted = batchHasTiktok && !batchTiktokSkipped;

  // Which TikTok-bound rows have a MEASURED duration over the account's limit
  // (§1c). Rows whose metadata hasn't loaded yet are simply unknown, never
  // assumed over — we only ever act on something we actually measured.
  //
  // Being over-length DROPS TikTok from that one post (see `scheduleAllBatch`
  // and the per-row note below); it never gates the queue. Same principle as a
  // failed creator_info fetch: one post's TikTok problem must not stop 36 other
  // posts — or that post's own YouTube and Instagram legs — from scheduling.
  const batchTooLong = useMemo(() => {
    if (!batchTiktokTargeted || tiktokMaxSec === null) return new Set<string>();
    return new Set(
      imported
        .filter((post) => effectivePlatforms(post).includes("tiktok"))
        .filter((post) => (batchDurations[post.folder] ?? 0) > tiktokMaxSec)
        .map((post) => post.folder),
    );
  }, [imported, batchDurations, batchTiktokTargeted, tiktokMaxSec, effectivePlatforms]);

  // Which YouTube-bound rows carry metadata YouTube would reject, and why —
  // keyed by folder so the row can print the reason and `scheduleAllBatch` can
  // act on the same verdict.
  //
  // Exactly the TikTok treatment above, for exactly the same reason: an unusable
  // title/description DROPS YouTube from that one post and nothing else, rather
  // than gating "Schedule all". Over-limit values can't be trimmed (Policies
  // III.C.1), and a MISSING title can't be sent at all — YouTube rejects an empty
  // `snippet.title` after the (multi-GB) upload, the exact "half published,
  // nobody watching" outcome we avoid by dropping the leg up front.
  //
  // Crucially this validates the text that will ACTUALLY be sent — the row's
  // edited/AI-generated `batchText` override wins over the sidecar, mirroring
  // `buildImportedDraft` — so editing a too-long title clears the warning and a
  // freshly-typed too-long one raises it. (Item A made the title editable, so the
  // old "fix it in post.yaml" dead-end no longer applies.)
  const batchYoutubeProblems = useMemo(() => {
    const byFolder = new Map<string, string[]>();
    if (!batchHasYoutube) return byFolder;
    for (const post of imported) {
      if (!effectivePlatforms(post).includes("youtube")) continue;
      const yt = post.data.platforms?.youtube ?? {};
      const override = batchText[post.folder]?.youtube;
      const title = override != null && override.trim() !== "" ? override : yt.title ?? "";
      // Description falls back to the title exactly as buildImportedDraft sends it.
      const text: YouTubeDraftText = { title, body: yt.description ?? title, tags: [] };
      const problems = youtubeLimitProblems(text, "batch");
      if (title.trim() === "") {
        problems.unshift("YouTube needs a title — add one in this post's YouTube box.");
      }
      if (problems.length > 0) byFolder.set(post.folder, problems);
    }
    return byFolder;
  }, [imported, batchHasYoutube, effectivePlatforms, batchText]);

  // Instagram and Facebook publish publicly no matter the visibility control, so
  // whenever this import actually lands on either, disclose it — even for a batch
  // that targets ONLY them, where the visibility section (and its note) never
  // renders. Meta's Platform Terms require consent before publishing on someone's
  // behalf, and a silently-public post is exactly what that guards against.
  const batchAlwaysPublic = useMemo(
    () =>
      alwaysPublicSelected(
        imported.flatMap((p) => effectivePlatforms(p)).filter((id) => connected.has(id)),
      ),
    [imported, connected, effectivePlatforms],
  );

  // Instagram's JPEG-only image rule (see `looksJpeg`), applied per row: a post
  // whose media is a non-JPEG image loses its Instagram leg and nothing else —
  // reported on the row, never gating the queue — exactly like the YouTube and
  // TikTok per-row skips above. Only rows whose Instagram is genuinely a target
  // (declared and connected) are considered.
  const batchInstagramProblems = useMemo(() => {
    const set = new Set<string>();
    if (!connected.has("instagram")) return set;
    for (const post of imported) {
      if (!effectivePlatforms(post).includes("instagram")) continue;
      if (fileIsNonJpegImage(post.mediaFile)) set.add(post.folder);
    }
    return set;
  }, [imported, connected, effectivePlatforms]);

  // What (if anything) blocks the batch from being scheduled. Mirrors
  // `tiktokBlocker` on the single-post path, and only ever applies when TikTok
  // is genuinely one of the targeted platforms — a YouTube/Instagram-only batch,
  // a batch whose TikTok isn't connected, and a batch whose TikTok is being
  // skipped are all never gated by it. Note what is NOT here: an over-long clip,
  // which degrades to a per-row skip instead of disabling "Schedule all".
  const batchTiktokBlocker: string | null = (() => {
    if (!batchTiktokTargeted) return null;
    if (tiktokLoading || !tiktokInfo) return "Loading TikTok account…";
    return tiktokSettingsBlocker(batchTt, "batch");
  })();

  // What pressing the button would actually send: every row that hasn't already
  // been scheduled. After a partial failure that is exactly the failures, and
  // the button says so rather than implying the whole queue goes again.
  const batchRemaining = useMemo(
    () => imported.filter((p) => batchStatus[p.folder]?.state !== "done").length,
    [imported, batchStatus],
  );
  const batchRetrying = batchRemaining < imported.length;

  // Schedule (or immediately dispatch) every imported post, SEQUENTIALLY. One
  // post failing never aborts the rest — failures are collected and reported.
  async function scheduleAllBatch() {
    // Belt-and-braces behind the disabled button: never send a TikTok batch
    // without an explicit creator-chosen privacy level, never send a YouTube
    // batch without the creator's own Made-for-Kids designation, and never
    // pick a visibility on their behalf.
    if (batchTiktokBlocker || batchYoutubeBlocker || batchVisibilityBlocker) return;

    setBatchRunning(true);
    setBatchSummary(null);
    setBatchFailed(false);

    // The creator's ONE explicit set of batch-wide choices, resolved through the
    // same builder the single-post flow uses (so per-account *_disabled flags
    // still force interactions off) and then attached to EVERY TikTok post's
    // draft below — one manual selection, applied 37 times.
    const tiktokOpts: TikTokOptions | null =
      batchTiktokTargeted && batchTt.privacyLevel
        ? buildTikTokOptions(batchTt, tiktokInfo)
        : null;

    // The creator's ONE explicit audience designation, attached to every YouTube
    // post below. Null only when YouTube isn't a target for this import — the
    // gate above guarantees an answer whenever it is.
    const madeForKids = madeForKidsValue(batchYt);
    const youtubeOpts: YouTubeOptions | null =
      batchHasYoutube && madeForKids !== null ? { madeForKids } : null;

    const failures: string[] = [];
    let ok = 0;

    for (const [index, post] of imported.entries()) {
      const folder = post.folder;
      const mark = (state: BatchState, error?: string) =>
        setBatchStatus((prev) => ({ ...prev, [folder]: { state, error } }));
      // A post that already landed is NEVER sent again. `batchStatus` is the
      // snapshot taken when this press started, so a retry attempts exactly the
      // rows that failed last time — the ones that succeeded are left alone
      // (and still counted, so the summary describes the whole queue).
      if (batchStatus[folder]?.state === "done") {
        ok += 1;
        continue;
      }
      try {
        // A platform is only ever a target when everything it requires is
        // satisfied, so a dropped platform can't sneak back into what is
        // actually sent — `buildImportedDraft` keys its per-platform text and
        // options off this list too. A clip measured over this account's TikTok
        // limit drops TikTok from THIS post only; metadata YouTube would reject
        // drops YouTube from THIS post only. In both cases the row's other
        // platforms, and every other row, are untouched.
        const targets = effectivePlatforms(post).filter(
          (id) =>
            connected.has(id) &&
            (id !== "tiktok" || (tiktokOpts !== null && !batchTooLong.has(folder))) &&
            (id !== "youtube" || !batchYoutubeProblems.has(folder)) &&
            // Non-JPEG image drops Instagram from THIS post only (Meta: JPEG-only).
            (id !== "instagram" || !batchInstagramProblems.has(folder)),
        );
        if (targets.length === 0) throw new Error("No connected target platforms.");
        if (!post.mediaFile) throw new Error("No media to upload.");

        mark("uploading");
        const media = await uploadMediaFile(post.mediaFile);
        const draft = buildImportedDraft(
          post,
          targets,
          media,
          tiktokOpts,
          batchCaptions[folder] ?? importedTiktokCaption(post),
          youtubeOpts,
          resolveVisibility(post),
          batchText[folder] ?? {},
        );
        const key = batchIdempotencyKey(importRunId, index, folder);

        if (batchPublishNow[folder]) {
          mark("publishing");
          await api.dispatch(targets, draft, key);
        } else {
          mark("scheduling");
          const at = batchTimes[folder] ? wallClockToEpoch(batchTimes[folder]!, timezone) : NaN;
          if (!Number.isFinite(at) || at <= Date.now()) {
            throw new Error("Pick a schedule time in the future.");
          }
          await api.schedule(targets, draft, at, key);
        }
        mark("done");
        ok += 1;
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Failed.";
        mark("failed", msg);
        failures.push(`${folder}: ${msg}`);
      }
    }

    onDispatched();
    setBatchRunning(false);
    if (failures.length === 0) {
      // Full success clears the batch panel; the popup is the confirmation.
      setImported([]);
      resetBatch();
      setBatchComplete({ ok, failed: 0 });
    } else {
      // Partial failure keeps the panel so the failed rows can be retried, and
      // the popup sends the creator to the Activity log for the per-post fix.
      setBatchFailed(true);
      setBatchSummary(
        `${ok} sent, ${failures.length} failed — ${failures.join("; ")}. ` +
          `Retrying sends only the failures; the posts already sent stay as they are.`,
      );
      setBatchComplete({ ok, failed: failures.length });
    }
  }

  // What (if anything) blocks a TikTok post right now — drives the button.
  const tiktokBlocker: string | null = (() => {
    if (!tiktokSelected) return null;
    if (tiktokLoading) return "Loading TikTok account…";
    if (tiktokError || !tiktokInfo) return "TikTok account info could not be loaded.";
    const settings = tiktokSettingsBlocker(ttSettings, "single");
    if (settings) return settings;
    // §1c — never send a clip longer than this account may post. Only a measured
    // duration counts against a real limit; metadata that hasn't loaded yet, and
    // an account with no usable limit, both block nothing.
    if (tiktokMaxSec !== null && mediaDurationSec !== null && mediaDurationSec > tiktokMaxSec) {
      return `This video is ${formatDuration(mediaDurationSec)} — longer than the ${formatDuration(tiktokMaxSec)} TikTok allows on this account.`;
    }
    return null;
  })();
  const tiktokReady = tiktokSelected ? tiktokBlocker === null : true;

  // What (if anything) blocks a YouTube upload right now — drives the button.
  // The audience designation comes first: it is the one thing Broadside must
  // never supply itself. Then the real limits, which are surfaced rather than
  // silently applied to the creator's text.
  const youtubeBlocker: string | null = (() => {
    if (!youtubeSelected) return null;
    return youtubeSettingsBlocker(ytSettings, "single") ?? youtubeLimitBlocker(youtubeText);
  })();
  const youtubeReady = youtubeSelected ? youtubeBlocker === null : true;

  // What (if anything) blocks an Instagram post right now — drives the button,
  // exactly like the TikTok/YouTube preconditions above. Instagram accepts JPEG
  // as its ONLY image format (Meta, see `looksJpeg`), so a non-JPEG image bound
  // for Instagram is caught here — surfaced at compose time with a readable
  // reason — instead of failing opaquely at Meta after upload. Videos (Reels)
  // are never affected; this is images only.
  const instagramSelected = selected.has("instagram");
  const instagramBlocker: string | null = (() => {
    if (!instagramSelected) return null;
    if (mediaKind === "image" && mediaUrl && !looksJpeg(mediaContentType, mediaUrl)) {
      return "Instagram accepts JPEG images only — convert this image to JPEG, or remove Instagram from the destinations.";
    }
    return null;
  })();
  const instagramReady = instagramSelected ? instagramBlocker === null : true;

  // Facebook connects the user, then needs a specific Page chosen before it can
  // publish (the server stores an empty accountId until one is picked). Catch
  // that here so it's surfaced at compose time — "pick a Page in Connections" —
  // instead of failing opaquely at publish.
  const facebookSelected = selected.has("facebook");
  const facebookBlocker: string | null = (() => {
    if (!facebookSelected) return null;
    const fb = accounts.find((a) => a.platform === "facebook");
    if (fb && !fb.accountId) {
      return "Pick a Facebook Page to publish to — open Connections, choose your Page, then try again.";
    }
    return null;
  })();
  const facebookReady = facebookSelected ? facebookBlocker === null : true;

  // Ask the server to tailor the caption/title/tags for each selected platform,
  // then prefill an editable review section. Never blocks the normal flow.
  async function tailorForEachPlatform() {
    setAiLoading(true);
    setAiError(null);
    const baseTags = tags
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    try {
      const res = await api.aiCaptions({ title, body, tags: baseTags }, [...selected]);
      if (!res.configured || !res.suggestions) {
        throw new Error("AI tailoring isn't enabled.");
      }
      const next: Partial<Record<PlatformId, AiEdit>> = {};
      for (const [platform, cap] of Object.entries(res.suggestions)) {
        if (!cap || !isPlatformId(platform)) continue;
        next[platform] = {
          title: cap.title,
          body: cap.body,
          tags: cap.tags.join(", "),
        };
      }
      setAiEdits(next);
      setEditsSource("ai");
    } catch (err) {
      setAiError(err instanceof Error ? err.message : "Couldn't tailor captions.");
    } finally {
      setAiLoading(false);
    }
  }

  // Update one field of one platform's AI-tailored caption (immutably).
  function updateAiEdit(platform: PlatformId, field: keyof AiEdit, value: string) {
    setAiEdits((prev) => {
      const current = prev[platform];
      if (!current) return prev;
      return { ...prev, [platform]: { ...current, [field]: value } };
    });
  }

  // Discard all per-platform overrides and post the shared draft everywhere.
  function resetAiEdits() {
    setAiEdits({});
    setAiError(null);
    setEditsSource(null);
  }

  // Build the perPlatform overrides for the draft, limited to platforms that are
  // both selected and have an AI-tailored edit.
  function buildPerPlatform(): Partial<Record<PlatformId, PerPlatformOverride>> | undefined {
    const out: Partial<Record<PlatformId, PerPlatformOverride>> = {};
    for (const platform of selected) {
      const edit = aiEdits[platform];
      if (!edit) continue;
      out[platform] = {
        title: edit.title,
        body: edit.body,
        tags: edit.tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
      };
    }
    return Object.keys(out).length > 0 ? out : undefined;
  }

  // AI-tailored review rows for the platforms currently selected.
  const aiRows = [...selected]
    .map((id) => ({ id, edit: aiEdits[id] }))
    .filter((r): r is { id: PlatformId; edit: AiEdit } => Boolean(r.edit));
  const platformName = (id: PlatformId) => platforms.find((p) => p.id === id)?.name ?? id;
  const canTailor =
    aiConfigured && selected.size > 0 && (title.trim().length > 0 || body.trim().length > 0);

  // Build the PostDraft from the current compose state. The single source of
  // truth for what this post IS — used by `publish` (dispatch/schedule) AND by
  // "Save draft", so a saved draft and a sent post are byte-for-byte the same.
  function buildDraft(): PostDraft {
    const perPlatform = buildPerPlatform();
    const madeForKids = madeForKidsValue(ytSettings);
    return {
      title,
      body,
      tags: tags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
      visibility,
      media: mediaUrl ? { kind: mediaKind, url: mediaUrl } : null,
      ...(tiktokSelected ? { tiktok: buildTikTokOptions(ttSettings, tiktokInfo) } : {}),
      ...(youtubeSelected && madeForKids !== null ? { youtube: { madeForKids } } : {}),
      ...(perPlatform ? { perPlatform } : {}),
    };
  }

  // Is there anything worth saving as a draft? A draft needs no title/platform
  // (that's the point — finish it later), but an empty one is pointless.
  const canSaveDraft =
    !savingDraft &&
    !busy &&
    (title.trim().length > 0 ||
      body.trim().length > 0 ||
      tags.trim().length > 0 ||
      mediaUrl.length > 0 ||
      selected.size > 0);

  // Save the current compose state as a draft. Carries `loadedDraftId` so saving
  // an already-loaded draft UPDATES it rather than piling up copies.
  async function saveDraft() {
    setSavingDraft(true);
    setError(null);
    try {
      const saved = await api.saveDraft({
        ...(loadedDraftId ? { id: loadedDraftId } : {}),
        title: title.trim() || null,
        platforms: [...selected],
        draft: buildDraft(),
      });
      setLoadedDraftId(saved.id);
      await reloadDrafts();
      setNotice("Draft saved.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't save draft.");
    } finally {
      setSavingDraft(false);
    }
  }

  // Delete a saved draft from the list (optimistic; reloads on failure).
  async function handleDeleteDraft(id: string) {
    if (!window.confirm("Delete this draft?")) return;
    setDrafts((prev) => prev.filter((d) => d.id !== id));
    if (loadedDraftId === id) setLoadedDraftId(null);
    try {
      await api.deleteDraft(id);
    } catch {
      void reloadDrafts();
    }
  }

  // --- Snippets: insert into the fields, save the current field as a snippet ---
  function insertCaptionSnippet(content: string) {
    setBody((prev) => (prev.trim() ? `${prev}\n${content}` : content));
  }
  function insertHashtagsSnippet(content: string) {
    setTags((prev) => (prev.trim() ? `${prev}, ${content}` : content));
  }
  async function saveCaptionSnippet() {
    if (!body.trim()) {
      setError("Write a caption before saving it.");
      return;
    }
    const name = window.prompt("Name this caption:")?.trim();
    if (!name) return;
    try {
      await api.saveSnippet({ kind: "caption", name, content: body });
      await reloadSnippets();
      setNotice(`Saved caption “${name}”.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't save snippet.");
    }
  }
  async function saveHashtagsSnippet() {
    if (!tags.trim()) {
      setError("Add some tags before saving them.");
      return;
    }
    const name = window.prompt("Name this hashtag group:")?.trim();
    if (!name) return;
    try {
      await api.saveSnippet({ kind: "hashtags", name, content: tags });
      await reloadSnippets();
      setNotice(`Saved hashtag group “${name}”.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't save snippet.");
    }
  }
  async function handleDeleteSnippet(id: string) {
    setSnippets((prev) => prev.filter((s) => s.id !== id));
    try {
      await api.deleteSnippet(id);
    } catch {
      void reloadSnippets();
    }
  }

  async function publish() {
    // Behind the disabled button: never upload without the creator's own
    // Made-for-Kids designation, never trim their text to fit, and never send a
    // non-JPEG image to Instagram (Meta accepts JPEG only).
    if (youtubeBlocker || instagramBlocker) return;
    // Editing a scheduled post must always take the SCHEDULE path — it has a
    // time. Guard against the immediate-dispatch branch if the time was cleared,
    // which would both publish now and orphan the original row.
    if (editing && !scheduleAt) {
      setError("Choose a schedule time to save changes to a scheduled post.");
      return;
    }
    setBusy(true);
    setError(null);
    // A fresh send supersedes any previous inline panel and its poll.
    stopDispatchPoll();
    setDispatchPanel(null);
    setNotice(null);
    const draft = buildDraft();
    try {
      if (scheduleAt) {
        const at = wallClockToEpoch(scheduleAt, timezone);
        if (!Number.isFinite(at) || at <= Date.now()) {
          throw new Error("Pick a schedule time in the future.");
        }
        const { scheduled } = await api.schedule([...selected], draft, at, idempotencyKey);
        // Reschedule = create the new row, THEN remove the old one — only after
        // the replacement exists, so a failure above can never orphan the post
        // (worst case leaves both, never neither). Cleared so a second press
        // can't double-unschedule an id that no longer exists.
        if (editId) {
          await api.unschedule(editId);
          setEditId(null);
        }
        setScheduleDone({
          when: formatInTz(scheduled.at, timezone, { dateStyle: "medium", timeStyle: "short" }),
          platforms: selected.size,
          rescheduled: Boolean(editId),
        });
        setScheduleAt("");
      } else {
        // Capture the key THIS dispatch went out under, before the draft-signature
        // effect can rotate it — the status poll must look up exactly this send.
        const dispatchKey = idempotencyKey;
        const targets: PlatformId[] = [...selected];
        const resp = await api.dispatch(targets, draft, dispatchKey);
        if (resp.queued) {
          // Publishing runs in the background (a multi-platform video can take
          // ~a minute). Show every target inline as "Publishing…" and poll for
          // the real outcome — the background publisher records one all-platforms
          // row, so the rows resolve together, no fake per-platform progress.
          setDispatchPanel({ platforms: targets, phase: "publishing", results: null });
          startDispatchPoll(dispatchKey);
          scheduleHistoryRefreshes();
          // Hand the key to the parent so the calendar/History keep refreshing
          // even if this panel is closed before the publish lands (#1). The
          // inline poll above still drives THIS panel while it's open.
          onPublishInFlight?.(dispatchKey);
        } else if (resp.results) {
          // A retry that landed after the first publish already recorded — the
          // real results are in hand, so reveal them straight away.
          setDispatchPanel({ platforms: targets, phase: "done", results: resp.results });
        }
      }
      onDispatched();
      // A loaded draft that's now been scheduled or dispatched is a real post —
      // remove it so it doesn't linger in the drafts list. Best-effort: a failed
      // delete just leaves the draft, never the send.
      if (loadedDraftId) {
        const draftId = loadedDraftId;
        setLoadedDraftId(null);
        try {
          await api.deleteDraft(draftId);
        } catch {
          /* best effort */
        }
        void reloadDrafts();
      }
      // Hand-off recorded: hold this key so a second press is a retry the server
      // recognises, not a second publish. The effect above releases it as soon
      // as the creator edits the draft into a different post. (Scheduling clears
      // `scheduleAt` just above, which is itself such an edit — so a completed
      // schedule frees the key immediately, as it always did.)
      keyPinned.current = true;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Publish failed");
    } finally {
      setBusy(false);
    }
  }

  const scheduling = scheduleAt.length > 0;
  // Connected platforms, and whether they're all currently selected — powers the
  // "Select all" / "Clear" toggle so a five-platform post is one click.
  const connectedPlatformIds = platforms.filter((p) => connected.has(p.id)).map((p) => p.id);
  const allConnectedSelected =
    connectedPlatformIds.length > 0 && connectedPlatformIds.every((id) => selected.has(id));
  // Credit gate: a publish spends a platform-weighted amount of credits per
  // selected platform (see lib/credits — kept in lockstep with the server).
  // Only enforced when billing reports a real balance (creditsBalance is a
  // number) — null means billing is off, so nothing is gated. Applies to
  // scheduled posts too: they spend the same credits when they fire.
  const creditsNeeded = creditCostForPlatforms(selected);
  const insufficientCredits =
    typeof creditsBalance === "number" &&
    creditsNeeded > 0 &&
    creditsBalance < creditsNeeded;
  // Only YouTube actually needs a title; TikTok/Instagram/X/Facebook publish the
  // caption (body). So require a title ONLY when YouTube is a target; otherwise
  // just require SOME text (title or caption) so the creator isn't forced to fill
  // a "Video title" field for a caption-only platform.
  const youtubeTargeted = selected.has("youtube");
  const hasText = title.trim().length > 0 || body.trim().length > 0;
  const textOk = youtubeTargeted ? title.trim().length > 0 : hasText;
  const canPublish =
    textOk &&
    selected.size > 0 &&
    !busy &&
    tiktokReady &&
    youtubeReady &&
    instagramReady &&
    facebookReady &&
    !insufficientCredits;
  // An immediate dispatch is still landing while its panel polls — keep the
  // button held and reading "Publishing…" for the whole window, not just the
  // brief `busy` spell before the 202 returns.
  const dispatchInFlight = dispatchPanel?.phase === "publishing";
  // Why the button is disabled, if it is — shown as a hint so a stuck user isn't
  // left guessing (#16). Ordered most-blocking first.
  const disabledReason: string | null = (() => {
    if (busy || dispatchInFlight) return null;
    if (connected.size === 0) return "Connect an account first, then you can publish.";
    if (youtubeTargeted && title.trim().length === 0) return "Add a title for YouTube before publishing.";
    if (!hasText) return "Add a caption before publishing.";
    if (selected.size === 0) return "Pick at least one platform to publish to.";
    if (insufficientCredits) {
      return `This post needs ${formatCredits(creditsNeeded)} credits but you have ${formatCredits(creditsBalance ?? 0)}.`;
    }
    return tiktokBlocker ?? youtubeBlocker ?? instagramBlocker ?? facebookBlocker;
  })();

  // The composer is a three-stage adaptation flow, not a plain scheduler box:
  //   1 Create  — a source idea (title/caption/media) + the platforms to hit
  //   2 Tailor  — Broadside rewrites a native version for each platform
  //   3 Review  — check each version, then publish/schedule everywhere
  // The stepper reflects real progress so the tailoring stage (the actual wedge)
  // reads as the heart of the flow rather than a buried button.
  const hasSource =
    Boolean(title.trim() || body.trim() || mediaUrl || uploadVideo) && selected.size > 0;
  const composeStage: 1 | 2 | 3 = !hasSource ? 1 : aiRows.length > 0 ? 3 : 2;
  // "Clear" lives next to "Import from folder" and is enabled only when there's
  // actually something to reset — single-post content or an in-progress edit.
  // (Batch/import has its own controls, so it's disabled there.)
  const canClear =
    imported.length === 0 &&
    (editing || Boolean(title || body || tags || mediaUrl || uploadVideo || scheduleAt));
  // Media-first: the caption/platforms/schedule/publish sections stay hidden
  // until the creator has actually added media (a chosen upload or a pasted
  // link), so a fresh composer isn't a wall of empty fields.
  const hasMedia = Boolean(mediaUrl || uploadVideo);

  return (
    <div className="card composer">
      <div className="composer-head">
        <div className="import-folder">
          <input
            ref={folderInputRef}
            type="file"
            multiple
            className="upload-input hidden-file"
            onChange={(e) => {
              void onFolderPicked(e.target.files);
              e.target.value = "";
            }}
          />
          {/* Primary, not secondary: bulk folder import is the main path into
              the composer, and as a `btn small` beside the heading it read as a
              footnote and went unnoticed. */}
          <button
            type="button"
            className="btn primary"
            onClick={() => folderInputRef.current?.click()}
          >
            Import from folder
          </button>
          <ImportHelp />
        </div>
        <button
          type="button"
          className="btn compose-clear"
          disabled={!canClear}
          onClick={() => {
            resetComposer();
            onExitEdit?.();
          }}
          title={
            canClear ? "Clear every field and start a fresh post" : "Nothing to clear yet"
          }
        >
          Clear
        </button>
      </div>

      {/* Three-stage framing: Create → Tailor → Review & publish. */}
      <ol className="compose-stepper" aria-label="Compose steps">
        {[
          { n: 1 as const, label: "Create", hint: "Your idea + channels" },
          { n: 2 as const, label: "Tailor", hint: "A native version each" },
          { n: 3 as const, label: "Review & publish", hint: "Check, then send" },
        ].map((s) => {
          const state = s.n < composeStage ? "done" : s.n === composeStage ? "active" : "todo";
          return (
            <li key={s.n} className={`compose-step is-${state}`} aria-current={state === "active" ? "step" : undefined}>
              <span className="compose-step-n" aria-hidden="true">
                {state === "done" ? "✓" : s.n}
              </span>
              <span className="compose-step-text">
                <span className="compose-step-label">{s.label}</span>
                <span className="compose-step-hint">{s.hint}</span>
              </span>
            </li>
          );
        })}
      </ol>

      {editing && (
        <div className="compose-editing" role="status">
          <span className="compose-editing-dot" aria-hidden="true">✎</span>
          Editing a scheduled post — saving reschedules it. Use “Clear” to start a fresh post.
        </div>
      )}

      {/* Saved drafts — reopen one into the composer, or delete it. Hidden while
          editing a scheduled post (a different mode) and when there are none. */}
      {!editing && drafts.length > 0 && (
        <div className="card import-list drafts-list">
          <div className="import-list-head">
            <strong>Drafts</strong>
            <span className="muted small">{drafts.length}</span>
          </div>
          {drafts.map((d) => {
            const isLoaded = loadedDraftId === d.id;
            return (
              <div key={d.id} className={`import-row ${isLoaded ? "loaded" : ""}`}>
                <div className="import-row-body">
                  <div className="conn-name">{draftLabel(d)}</div>
                  <div className="muted small">
                    {d.platforms.length > 0 ? d.platforms.join(", ") : "no platforms"} · saved{" "}
                    {new Date(d.updatedAt).toLocaleDateString()}
                  </div>
                </div>
                <button
                  type="button"
                  className="btn small"
                  disabled={uploading}
                  onClick={() => loadDraft(d)}
                >
                  {isLoaded ? "Loaded ✓" : "Load"}
                </button>
                <button
                  type="button"
                  className="btn small ghost"
                  aria-label={`Delete draft ${draftLabel(d)}`}
                  onClick={() => void handleDeleteDraft(d.id)}
                >
                  Delete
                </button>
              </div>
            );
          })}
        </div>
      )}

      {importError && <p className="error">{importError}</p>}

      {imported.length === 1 && (
        <div className="card import-list">
          <div className="import-list-head">
            <strong>1 post found</strong>
            <span className="muted small">{importRoot}</span>
          </div>
          {imported.map((post) => {
            const targets = platformsInPost(post.data);
            const isLoaded = loadedFolder === post.folder;
            return (
              <div key={post.folder} className={`import-row ${isLoaded ? "loaded" : ""}`}>
                <div className="import-row-body">
                  <div className="conn-name">{post.folder}</div>
                  <div className="muted small">
                    {post.mediaFile ? post.mediaFile.name : "no video"}
                    {targets.length > 0 && ` · ${targets.join(", ")}`}
                  </div>
                  {post.warnings.map((w, i) => (
                    <div key={i} className="muted small warn">
                      ⚠ {w}
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  className="btn small"
                  disabled={uploading}
                  onClick={() => void loadImportedPost(post)}
                >
                  {isLoaded ? "Loaded ✓" : "Load"}
                </button>
              </div>
            );
          })}
          {importSkipped.length > 0 && (
            <p className="muted small">
              Skipped {importSkipped.length} folder(s) — they had no post details file (post.yaml).
            </p>
          )}
        </div>
      )}

      {batchMode && (
        <div className="card import-list batch-list">
          <div className="import-list-head">
            <strong>{imported.length} posts found</strong>
            <span className="muted small">{importRoot}</span>
          </div>
          <fieldset className="batch-cadence">
            <legend>Posting cadence</legend>
            {BATCH_CADENCES.map(({ perDay, label }) => (
              <label key={perDay} className="batch-cadence-opt">
                <input
                  type="radio"
                  name="batch-cadence"
                  value={perDay}
                  checked={batchPerDay === perDay}
                  disabled={batchRunning}
                  onChange={() => chooseBatchPerDay(perDay)}
                />
                <span>{label}</span>
              </label>
            ))}
            {/* Editable fill-in time(s) for the chosen cadence — pre-filled, but
                yours to change. Each row's exact time is still editable below. */}
            <span className="batch-cadence-times">
              <span className="muted">at</span>
              {batchSlotTimes.map((t, i) => (
                <span key={i} className="batch-cadence-time-wrap">
                  {i > 0 && <span className="muted">&amp;</span>}
                  <input
                    type="time"
                    className="batch-cadence-time"
                    value={t}
                    disabled={batchRunning}
                    aria-label={`Fill-in time ${i + 1}`}
                    onChange={(e) => chooseBatchSlotTime(i, e.target.value)}
                  />
                </span>
              ))}
            </span>
            {batchLastTime && (
              <span className="muted small batch-cadence-summary">
                {imported.length} posts · last publishes {END_DATE_FORMAT.format(batchLastTime)}
              </span>
            )}
          </fieldset>
          <p className="muted small">
            Suggestions start tomorrow. Nothing publishes until you press “Schedule all”.
          </p>

          {/* Item A — batch-wide platform default. Applies to every post that
              doesn't name its own platforms in post.yaml or set a per-row
              override, so a folder of bare videos still publishes somewhere. */}
          <fieldset className="batch-platforms">
            <legend>Post these to</legend>
            <div className="batch-platform-pills">
              {ALL_PLATFORM_IDS.map((id) => {
                const info = platforms.find((pf) => pf.id === id);
                const isConnected = connected.has(id);
                const on = batchPlatforms.includes(id);
                return (
                  <button
                    key={id}
                    type="button"
                    className={`plat-pill${on ? " on" : ""}${isConnected ? "" : " off"}`}
                    disabled={batchRunning || !isConnected}
                    aria-pressed={on}
                    title={isConnected ? undefined : `${info?.name ?? id} isn't connected`}
                    onClick={() => toggleBatchPlatform(id)}
                  >
                    {info && <PlatformIcon platform={info} size={15} />}
                    <span>{info?.name ?? id}</span>
                  </button>
                );
              })}
            </div>
            <p className="muted small batch-platforms-help">
              The default for every post below. A post whose post.yaml names platforms keeps them;
              any row can override this with its own pills.
            </p>
          </fieldset>

          {batchDeclaresYoutube && !connected.has("youtube") && (
            <p className="muted small warn">
              ⚠ YouTube isn't connected — these posts will be scheduled without YouTube.
            </p>
          )}

          {batchDeclaresTiktok && !connected.has("tiktok") && (
            <p className="muted small warn">
              ⚠ TikTok isn't connected — these posts will be scheduled without TikTok.
            </p>
          )}

          {batchTiktokSkipped && (
            <p className="muted small warn">
              ⚠ TikTok will be skipped for this batch — its account info couldn't be loaded (
              {tiktokError}). Everything else will still be scheduled.{" "}
              <button
                type="button"
                className="btn small"
                disabled={batchRunning}
                onClick={retryTiktokInfo}
              >
                Retry
              </button>
            </p>
          )}

          {batchTiktokTargeted && tiktokLoading && <p className="muted small">Loading TikTok account…</p>}

          {/* Meta consent gap: disclosed once per import, independent of the
              visibility section below (which only renders when YouTube or TikTok
              is a target). An Instagram/Facebook-only queue publishes publicly
              and must say so even though no visibility control is shown. */}
          <PublicOnlyNotice platforms={batchAlwaysPublic} />

          {/* One explicit visibility for the whole import, nothing preselected.
              A post that names its own `visibility:` keeps it; the rest take
              this. Rendered only where something honours it, and beside the
              note saying which platforms those are. */}
          {batchHonoursVisibility && (
            <>
              <VisibilityPicker
                scope="batch"
                value={batchVisibility}
                onChange={setBatchVisibility}
                disabled={batchRunning}
              />
              <p className="muted small">
                Applied to every post in this import, except any that set their own visibility in
                their details file (<code>post.yaml</code>).
              </p>
            </>
          )}

          {/* The same YouTube panel the single-post flow renders — one explicit
              audience designation that the loop below attaches to every YouTube
              upload in this import. */}
          {batchHasYoutube && (
            <YouTubePanel
              channelName={youtubeChannel}
              scope="batch"
              value={batchYt}
              onChange={setBatchYt}
              disabled={batchRunning}
            />
          )}

          {/* The same options panel the single-post flow renders — one explicit
              choice that the loop below attaches to every TikTok post. */}
          {batchTiktokTargeted && tiktokInfo && (
            <TikTokPanel
              info={tiktokInfo}
              scope="batch"
              value={batchTt}
              onChange={setBatchTt}
              disabled={batchRunning}
            />
          )}

          {imported.map((post) => {
            const targets = effectivePlatforms(post);
            const declaredInYaml = platformsInPost(post.data).length > 0;
            const usingOverride = batchPlatformOverride[post.folder] != null;
            const publishNow = batchPublishNow[post.folder] ?? false;
            const status = batchStatus[post.folder];
            // A row is measured against TikTok's limit only when TikTok will
            // actually receive it.
            const rowHasTiktok = batchTiktokTargeted && targets.includes("tiktok");
            // …and TikTok drops off this row entirely once the clip measures over.
            const rowTiktokTooLong = batchTooLong.has(post.folder);
            // …and YouTube drops off this row entirely once its metadata is over
            // a limit. Over-limit text is REPORTED, never trimmed (Developer
            // Policies III.C.1), and never gates the queue: the creator fixes
            // the sidecar, and meanwhile every other row still schedules.
            const rowYoutubeProblems = batchYoutubeProblems.get(post.folder) ?? [];
            // …and Instagram drops off this row when the media is a non-JPEG
            // image (Meta accepts JPEG only). Same per-row treatment: the post's
            // other platforms and every other row still schedule.
            const rowInstagramNonJpeg = batchInstagramProblems.has(post.folder);
            // Instagram / Facebook / X will happily post with no caption — for a
            // bare folder that's easy to do by accident, so name the platforms
            // whose resolved caption (edited box → sidecar) is still empty. It's
            // informational, not a gate: a captionless post is valid, just rarely
            // intended. (TikTok has its own box; YouTube's empty title is already
            // a hard per-row skip above.)
            const captionlessPlatforms = targets.filter((id) => {
              if (id !== "instagram" && id !== "facebook" && id !== "x") return false;
              if (!connected.has(id)) return false;
              const p = post.data.platforms ?? {};
              const sidecar =
                id === "instagram" ? p.instagram?.caption : id === "x" ? p.x?.text : p.facebook?.caption;
              const edited = batchText[post.folder]?.[id];
              return !((edited ?? "").trim() || (sidecar ?? "").trim());
            });
            return (
              <div key={post.folder} className="import-row">
                <VideoPreview
                  file={post.mediaFile}
                  maxDurationSec={rowHasTiktok ? tiktokMaxSec : null}
                  onDuration={(seconds) =>
                    setBatchDurations((prev) => ({ ...prev, [post.folder]: seconds }))
                  }
                />
                <div className="import-row-body">
                  <div className="conn-name">{post.folder}</div>
                  <div className="muted small">
                    {post.mediaFile ? post.mediaFile.name : "no video"}
                    {targets.length > 0 && ` · ${targets.join(", ")}`}
                  </div>
                  {post.warnings.map((w, i) => (
                    <div key={i} className="muted small warn">
                      ⚠ {w}
                    </div>
                  ))}
                  {/* Metadata YouTube would reject costs this post its YouTube
                      leg and nothing else — say so on the row, naming the limit
                      and the measurement, rather than gating the whole queue on
                      a value that can only be changed in post.yaml. */}
                  {rowYoutubeProblems.length > 0 && (
                    <div className="muted small warn">
                      ⚠ YouTube will be skipped for this post — {rowYoutubeProblems.join(" ")} Its
                      other platforms and the rest of the queue are unaffected.
                    </div>
                  )}
                  {/* A non-JPEG image costs this post its Instagram leg and
                      nothing else — Instagram accepts JPEG images only (Meta) —
                      rather than gating the whole queue on a file swap. */}
                  {rowInstagramNonJpeg && (
                    <div className="muted small warn">
                      ⚠ Instagram will be skipped for this post — Instagram accepts JPEG images
                      only, and this file isn't a JPEG. Its other platforms and the rest of the
                      queue are unaffected.
                    </div>
                  )}
                  {/* An over-long clip costs this post its TikTok leg and
                      nothing else — say so on the row, next to the offending
                      video, rather than gating the whole queue. */}
                  {rowTiktokTooLong && tiktokMaxSec !== null && (
                    <div className="muted small warn">
                      ⚠ TikTok will be skipped for this post —{" "}
                      {formatDuration(batchDurations[post.folder] ?? 0)} is longer than this
                      account's {formatDuration(tiktokMaxSec)} limit. Its other platforms and
                      the rest of the queue are unaffected.
                    </div>
                  )}
                  {captionlessPlatforms.length > 0 && (
                    <div className="muted small warn">
                      ⚠ No caption yet for{" "}
                      {captionlessPlatforms
                        .map((id) => platforms.find((pf) => pf.id === id)?.name ?? id)
                        .join(", ")}{" "}
                      — {captionlessPlatforms.length === 1 ? "it" : "they"} will post without text.
                      Add one below or use “✨ AI captions”.
                    </div>
                  )}
                  {/* Item A — choose platforms + write/generate captions on the
                      site. The pills override the batch-wide default for THIS
                      post; the idea line feeds AI caption generation. */}
                  <div className="batch-edit">
                    <div className="batch-edit-plats">
                      <span className="batch-edit-label">Platforms</span>
                      <div className="batch-platform-pills">
                        {ALL_PLATFORM_IDS.map((id) => {
                          const info = platforms.find((pf) => pf.id === id);
                          const isConnected = connected.has(id);
                          const on = targets.includes(id);
                          return (
                            <button
                              key={id}
                              type="button"
                              className={`plat-pill sm${on ? " on" : ""}${
                                isConnected ? "" : " off"
                              }`}
                              disabled={batchRunning || !isConnected}
                              aria-pressed={on}
                              title={isConnected ? undefined : `${info?.name ?? id} isn't connected`}
                              onClick={() => toggleRowPlatform(post, id)}
                            >
                              {info && <PlatformIcon platform={info} size={14} />}
                              <span>{info?.name ?? id}</span>
                            </button>
                          );
                        })}
                      </div>
                      {usingOverride && (
                        <button
                          type="button"
                          className="btn ghost small"
                          disabled={batchRunning}
                          onClick={() => clearRowOverride(post.folder)}
                        >
                          Reset to {declaredInYaml ? "file" : "default"}
                        </button>
                      )}
                    </div>

                    <div className="batch-edit-idea">
                      <input
                        type="text"
                        className="batch-idea-input"
                        placeholder="What's this post about? (seeds the captions)"
                        value={batchIdea[post.folder] ?? ""}
                        disabled={batchRunning || batchAiBusy[post.folder]}
                        onChange={(e) =>
                          setBatchIdea((prev) => ({ ...prev, [post.folder]: e.target.value }))
                        }
                      />
                      {aiConfigured && (
                        <button
                          type="button"
                          className="btn small"
                          disabled={batchRunning || batchAiBusy[post.folder]}
                          onClick={() => void generateRowCaptions(post)}
                        >
                          {batchAiBusy[post.folder] ? "Generating…" : "✨ AI captions"}
                        </button>
                      )}
                    </div>
                    {batchAiError[post.folder] && (
                      <p className="error small">{batchAiError[post.folder]}</p>
                    )}

                    {/* One caption box per targeted, connected non-TikTok platform.
                        TikTok keeps its dedicated box below (its length rules and
                        the `batchCaptions` store are wired to it). */}
                    {targets
                      .filter((id) => id !== "tiktok" && connected.has(id))
                      .map((id) => {
                        const info = platforms.find((pf) => pf.id === id);
                        return (
                          <label key={id} className="field batch-caption">
                            <span>
                              {info?.name ?? id}
                              {id === "youtube" ? " title" : " caption"}
                            </span>
                            <textarea
                              rows={2}
                              value={batchText[post.folder]?.[id] ?? ""}
                              disabled={batchRunning}
                              placeholder={`Caption for ${info?.name ?? id}`}
                              onChange={(e) => setRowText(post.folder, id, e.target.value)}
                            />
                          </label>
                        );
                      })}
                  </div>

                  {/* §5b — the creator edits the caption that is actually posted;
                      `scheduleAllBatch` reads this value, not the sidecar's. A
                      row whose TikTok leg is dropped gets no caption box: it
                      would edit text that is never sent. */}
                  {rowHasTiktok && !rowTiktokTooLong && (
                    <label className="field batch-caption">
                      <span>TikTok caption</span>
                      <textarea
                        rows={2}
                        value={batchCaptions[post.folder] ?? ""}
                        disabled={batchRunning}
                        placeholder="Caption for TikTok"
                        onChange={(e) =>
                          setBatchCaptions((prev) => ({ ...prev, [post.folder]: e.target.value }))
                        }
                      />
                    </label>
                  )}
                  <div className="batch-controls">
                    <input
                      type="datetime-local"
                      className="batch-time"
                      value={batchTimes[post.folder] ?? ""}
                      disabled={publishNow || batchRunning}
                      aria-label={`Schedule date and time for ${post.folder}`}
                      onChange={(e) =>
                        setBatchTimes((prev) => ({ ...prev, [post.folder]: e.target.value }))
                      }
                    />
                    <label className="batch-now small">
                      <input
                        type="checkbox"
                        checked={publishNow}
                        disabled={batchRunning}
                        onChange={(e) =>
                          setBatchPublishNow((prev) => ({
                            ...prev,
                            [post.folder]: e.target.checked,
                          }))
                        }
                      />
                      Publish now
                    </label>
                    {status && (
                      <span className={`batch-status ${status.state}`}>
                        {BATCH_STATUS_LABEL[status.state]}
                        {status.state === "failed" && status.error ? ` — ${status.error}` : ""}
                      </span>
                    )}
                  </div>
                  {!publishNow && batchConflicts.has(post.folder) && (
                    <div className="batch-conflict" role="alert">
                      <span className="batch-conflict-msg">
                        ⚠ That day already has a post scheduled — both will go out together
                        unless you move this one.
                      </span>
                      <button
                        type="button"
                        className="btn ghost small"
                        disabled={batchRunning}
                        onClick={() =>
                          setBatchTimes((prev) => ({
                            ...prev,
                            [post.folder]: batchConflicts.get(post.folder)!.suggested,
                          }))
                        }
                      >
                        Move to{" "}
                        {END_DATE_FORMAT.format(
                          new Date(batchConflicts.get(post.folder)!.suggested),
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {importSkipped.length > 0 && (
            <p className="muted small">
              Skipped {importSkipped.length} folder(s) — they had no video and no post details file
              (post.yaml).
            </p>
          )}

          {batchSummary && (
            <p className={batchFailed ? "error" : "notice"}>{batchSummary}</p>
          )}

          {batchTiktokBlocker && <p className="error">{batchTiktokBlocker}</p>}
          {batchYoutubeBlocker && <p className="error">{batchYoutubeBlocker}</p>}
          {batchVisibilityBlocker && <p className="error">{batchVisibilityBlocker}</p>}

          {/* ToS §9.1 — the required notice, beside the control that commits
              these uploads. The batch commits uploads too, so it carries it. */}
          {batchHasYoutube && <YouTubeUploadNotice />}

          <div className="batch-actions">
            <button
              type="button"
              className="btn primary"
              disabled={
                batchRunning ||
                batchRemaining === 0 ||
                batchTiktokBlocker !== null ||
                batchYoutubeBlocker !== null ||
                batchVisibilityBlocker !== null
              }
              onClick={() => void scheduleAllBatch()}
            >
              {batchRunning
                ? "Working…"
                : batchRetrying
                  ? `Retry ${batchRemaining} failed post${batchRemaining === 1 ? "" : "s"}`
                  : `Schedule all ${imported.length} posts`}
            </button>
          </div>
        </div>
      )}

      {/* The manual single-post composer. Hidden during a folder batch so the
          batch has exactly one action ("Schedule all N posts") — showing this
          empty composer alongside it (with its own "Dispatch to N platforms"
          button) made it ambiguous which control sent the imported posts. */}
      {!batchMode && (
      <>
      {connected.size === 0 && (
        <div className="compose-empty">
          <div>
            <strong>Connect an account to start publishing.</strong>
            <p className="muted small">
              You haven&apos;t connected any accounts yet — connect YouTube, TikTok, Instagram,
              Facebook or X and you can post to all of them at once.
            </p>
          </div>
          {onConnectAccounts && (
            <button type="button" className="btn primary" onClick={onConnectAccounts}>
              Connect an account →
            </button>
          )}
        </div>
      )}
      {uploadConfigured ? (
        <div className="field">
          <span>Upload a video or image (up to 2GB)</span>
          <label
            className={`upload-drop ${dragActive ? "drag" : ""} ${uploading ? "busy" : ""}`}
            onDragOver={(e) => {
              e.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={() => setDragActive(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragActive(false);
              // Ignore drops while an upload is already running (mirrors the
              // handleFile guard + the disabled file input).
              if (uploading) return;
              const f = e.dataTransfer.files?.[0];
              if (f) void handleFile(f);
            }}
          >
            <input
              type="file"
              accept="video/mp4,video/quicktime,video/webm,image/jpeg,image/png,image/webp,image/gif"
              className="upload-input"
              disabled={uploading}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void handleFile(f);
                e.target.value = "";
              }}
            />
            {uploadName ? (
              <span className="upload-file">
                <span className="upload-name">{uploadName}</span>
                {uploadDone && <span className="upload-check">uploaded ✓</span>}
              </span>
            ) : (
              <span className="upload-hint">
                <strong>Drop a file</strong> or click to browse
              </span>
            )}
          </label>
          {uploading && (
            <div className="upload-bar">
              <div className="upload-bar-fill" style={{ width: `${uploadPct}%` }} />
            </div>
          )}
          {uploadName && !uploading && (
            <button type="button" className="btn small upload-clear" onClick={clearUpload}>
              Remove
            </button>
          )}
          {uploadError && <p className="error">{uploadError}</p>}
        </div>
      ) : (
        <p className="muted small">
          File upload isn't set up yet — paste a public media URL below instead.
        </p>
      )}

      <label className="field">
        <span>Media URL (public link to your video/image)</span>
        <input
          value={mediaUrl}
          onChange={(e) => {
            setMediaUrl(e.target.value);
            // A hand-typed URL has no known content type; let the JPEG gate read
            // the extension instead of a stale type from a prior upload.
            setMediaContentType(null);
          }}
          placeholder="https://…/clip.mp4"
        />
      </label>

      {/* §5a — the creator sees the clip (and its length) before it is sent. */}
      <VideoPreview
        file={previewFile}
        src={previewSrc}
        maxDurationSec={tiktokSelected ? tiktokMaxSec : null}
        onDuration={setMediaDurationSec}
      />

      {/* Media-first reveal: everything below appears only once there's a video
          or image (uploaded or linked). Keeps step 1 focused on adding media. */}
      {hasMedia && (
        <>
      <label className="field">
        <span>Title</span>
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Video title" />
      </label>

      <div className="field">
        <div className="field-head">
          <span>Description / caption</span>
          <SavedMenu
            label="caption"
            snippets={captionSnippets}
            onInsert={insertCaptionSnippet}
            onSaveCurrent={() => void saveCaptionSnippet()}
            onDelete={(id) => void handleDeleteSnippet(id)}
            emptyHint="No saved captions yet — save the one above to reuse it."
          />
        </div>
        <textarea
          rows={5}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="What's this about?"
          aria-label="Description / caption"
        />
      </div>

      <div className="row">
        <div className="field grow">
          <div className="field-head">
            <span>Tags (comma-separated)</span>
            <SavedMenu
              label="hashtag group"
              snippets={hashtagSnippets}
              onInsert={insertHashtagsSnippet}
              onSaveCurrent={() => void saveHashtagsSnippet()}
              onDelete={(id) => void handleDeleteSnippet(id)}
              emptyHint="No saved hashtag groups yet — save the tags above to reuse them."
            />
          </div>
          <input
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder="shorts, ai"
            aria-label="Tags (comma-separated)"
          />
        </div>
        {/* The same picker the batch renders. Unlike the batch this one starts
            on "Private" — a single post is composed in front of the control, so
            the value is always on screen before anything is sent. */}
        <VisibilityPicker
          scope="single"
          value={visibility}
          onChange={(next) => {
            if (next) setVisibility(next);
          }}
        />
      </div>
      {/* Meta consent gap: whenever Instagram or Facebook is an actual
          destination, say plainly that it publishes publicly no matter the
          setting above. Reactive to the platform picker below. */}
      <PublicOnlyNotice platforms={alwaysPublicSelected(selected)} />

      <div className="field">
        <span>Schedule</span>
        {/* One control: Publish-now / Schedule, and when scheduling, a single
            calendar-plus-time panel. Sets the same wall-clock scheduleAt string. */}
        <SchedulePicker
          value={scheduleAt}
          onChange={setScheduleAt}
          busy={busyPosts}
          timezone={timezone}
        />
      </div>

      <div className="field">
        <div className="picker-head">
          <span>Publish to</span>
          {connectedPlatformIds.length > 1 && (
            <button
              type="button"
              className="select-all-btn"
              onClick={() =>
                setSelected(allConnectedSelected ? new Set() : new Set(connectedPlatformIds))
              }
            >
              {allConnectedSelected ? "Clear all" : "Select all"}
            </button>
          )}
        </div>
        <div className="platform-picker">
          {platforms.map((p) => {
            const isConnected = connected.has(p.id);
            const isOn = selected.has(p.id);
            return (
              <button
                key={p.id}
                type="button"
                className={`pill ${isOn ? "on" : ""} ${isConnected ? "" : "disabled"}`}
                style={isOn ? { borderColor: p.accent, boxShadow: `0 0 0 1px ${p.accent}` } : {}}
                disabled={!isConnected}
                aria-pressed={isOn}
                title={isConnected ? "" : `Connect ${p.name} first`}
                onClick={() => toggle(p.id)}
              >
                <PlatformIcon platform={p} size={20} />
                {p.name}
              </button>
            );
          })}
        </div>
      </div>

      {/* Native trending audio is one of the biggest reach levers on Shorts &
          Reels, but the publishing APIs can't attach it — it's added in each
          platform's own app after posting. Quiet, non-blocking hint (not a nag):
          only when a short-video platform is actually selected. */}
      {[...selected].some((id) => SOUND_HINT_PLATFORMS.has(id)) && (
        <p className="muted small compose-sound-hint">
          <span aria-hidden="true">🎵</span> Tip: Shorts &amp; Reels reach more people with a
          trending sound. Add one in the app after posting — it can’t be set through the upload.
        </p>
      )}

      {editsSource === "import" && importMissing.length > 0 && (
        <p className="muted small warn">
          ⚠ Your file also has text for {importMissing.map(platformName).join(", ")}, but{" "}
          {importMissing.length === 1 ? "that platform isn’t" : "those platforms aren’t"} connected
          yet — {importMissing.length === 1 ? "it’s" : "they’re"} skipped for now. Connect{" "}
          {importMissing.length === 1 ? "it" : "them"} on the Connections page to include{" "}
          {importMissing.length === 1 ? "it" : "them"}.
        </p>
      )}

      {(aiConfigured || aiRows.length > 0) && (
        <div className="ai-tailor">
          {aiConfigured && (
            <div className="ai-tailor-row">
              <button
                type="button"
                className="btn ai-btn"
                disabled={!canTailor || aiLoading}
                onClick={tailorForEachPlatform}
                title={
                  canTailor
                    ? "Rewrite the hook, caption and title for each platform"
                    : "Select a platform and add a title or caption first"
                }
              >
                {aiLoading ? "Tailoring…" : "Tailor for each platform"}
              </button>
              <InfoHint label="What “Tailor for each platform” does">
                <p>
                  This is the part that isn’t just cross-posting: Broadside rewrites your hook,
                  caption, title and tags into a{" "}
                  <strong>native version for each platform</strong> — then you review every one
                  before it goes out.
                </p>
              </InfoHint>
            </div>
          )}
          {aiError && <p className="error">{aiError}</p>}

          {aiRows.length > 0 && (
            <div className="ai-review">
              {aiRows.map(({ id, edit }) => (
                <div key={id} className="card ai-card">
                  <div className="ai-card-head">
                    <strong>{platformName(id)}</strong>
                    <span className="muted small">
                      {editsSource === "import" ? "From folder" : "AI-tailored"} — edit before posting
                    </span>
                  </div>
                  {aiCardFields(id).map((f) => (
                    <label key={f.key} className="field">
                      <span>{f.label}</span>
                      {f.multiline ? (
                        <textarea
                          rows={3}
                          value={edit[f.key]}
                          onChange={(e) => updateAiEdit(id, f.key, e.target.value)}
                        />
                      ) : (
                        <input
                          value={edit[f.key]}
                          onChange={(e) => updateAiEdit(id, f.key, e.target.value)}
                        />
                      )}
                    </label>
                  ))}
                </div>
              ))}
              <button type="button" className="btn small ai-reset" onClick={resetAiEdits}>
                Reset to original
              </button>
            </div>
          )}
        </div>
      )}

      {youtubeSelected && (
        <YouTubePanel
          channelName={youtubeChannel}
          scope="single"
          value={ytSettings}
          onChange={setYtSettings}
          text={youtubeText}
        />
      )}

      {tiktokSelected && (
        <>
          {tiktokLoading && <p className="muted small">Loading TikTok account…</p>}
          {tiktokError && <p className="error">{tiktokError}</p>}
          {tiktokInfo && (
            <TikTokPanel
              info={tiktokInfo}
              scope="single"
              value={ttSettings}
              onChange={setTtSettings}
            />
          )}
        </>
      )}

      {error && <p className="error">{error}</p>}
      {notice && <p className="notice">{notice}</p>}
      {tiktokSelected && tiktokBlocker && <p className="error">{tiktokBlocker}</p>}
      {youtubeBlocker && <p className="error">{youtubeBlocker}</p>}
      {instagramBlocker && <p className="error">{instagramBlocker}</p>}
        </>
      )}

      {/* Live inline dispatch results: every target platform appears the moment
          Dispatch is pressed, showing a spinner until the background publisher's
          one all-platforms row lands, then each platform's real outcome (Posted +
          link, or Failed + reason) — no optimistic green that hides failures. */}
      {dispatchPanel && (() => {
        const results = dispatchPanel.results;
        const done = dispatchPanel.phase !== "publishing";
        const allOk = Boolean(results && results.length > 0 && results.every((r) => r.ok));
        // A hard failure = a real error, not a "held/pending approval" leg.
        const anyHardFail = Boolean(
          results && results.some((r) => !r.ok && !isPendingPlatformError(r.error)),
        );
        return (
          <div
            className="batch-done-backdrop"
            role="dialog"
            aria-modal="true"
            aria-label={done ? "Publish results" : "Publishing"}
            onClick={() => {
              // Mid-publish the backdrop never dismisses — the overlay is the only
              // place the live progress shows, and "Hide" is the deliberate exit.
              // Once done, a backdrop click closes it (a full success clears the
              // draft too, so the creator isn't left on a post that already went).
              if (!done) return;
              if (allOk) resetComposer();
              else setDispatchPanel(null);
            }}
          >
            <div className="batch-done publish-live" onClick={(e) => e.stopPropagation()}>
              {!done ? (
                <>
                  <div className="batch-done-spinner" aria-hidden="true" />
                  <h3 className="batch-done-title">Firing…</h3>
                </>
              ) : (
                <>
                  <div className={`batch-done-icon ${anyHardFail ? "warn" : "ok"}`}>
                    {anyHardFail ? "!" : "✓"}
                  </div>
                  <h3 className="batch-done-title">
                    {dispatchPanel.phase === "timeout"
                      ? "Still publishing"
                      : anyHardFail
                        ? "Published — with issues"
                        : "Published!"}
                  </h3>
                </>
              )}

              <ul className="dispatch-rows" role="status" aria-live="polite">
                {/* Ordered like the picker (iterate the full platform list),
                    limited to this dispatch's targets. */}
                {platforms
                  .filter((p) => dispatchPanel.platforms.includes(p.id))
                  .map((p, i) => {
                    const result = results?.find((r) => r.platform === p.id) ?? null;
                    const state = result
                      ? result.ok
                        ? "ok"
                        : isPendingPlatformError(result.error)
                          ? "held"
                          : "err"
                      : "pending";
                    return (
                      <li
                        key={p.id}
                        className={`dispatch-row ${state}`}
                        // Staggered reveal once results land — purely cosmetic; the
                        // platforms genuinely complete together (one recorded row).
                        style={result ? { animationDelay: `${i * 60}ms` } : undefined}
                      >
                        <PlatformIcon platform={p} size={22} />
                        <span className="dispatch-row-name">{p.name}</span>
                        {state === "pending" ? (
                          <span className="dispatch-row-status">
                            <span className="dispatch-spinner" aria-hidden="true" />
                            Publishing…
                          </span>
                        ) : state === "ok" ? (
                          <span className="dispatch-row-status ok">
                            <span aria-hidden="true">✓</span> Posted
                            {result?.url && (
                              <a
                                href={result.url}
                                target="_blank"
                                rel="noreferrer"
                                className="dispatch-link"
                              >
                                View post ↗
                              </a>
                            )}
                          </span>
                        ) : state === "held" ? (
                          <span className="dispatch-row-status held">Not publishing yet</span>
                        ) : (
                          <span className="dispatch-row-status err">
                            <span aria-hidden="true">✕</span> Failed —{" "}
                            {humanizeDispatchError(result?.error)}
                            {isInsufficientCreditsError(result?.error) && onBuyCredits && (
                              <button
                                type="button"
                                className="link-btn"
                                onClick={onBuyCredits}
                              >
                                Buy credits
                              </button>
                            )}
                          </span>
                        )}
                      </li>
                    );
                  })}
              </ul>

              {!done ? (
                <>
                  <p className="muted batch-done-body">
                    You can leave this page — publishing keeps going in the background, and
                    you'll get a notification the moment it's done.
                  </p>
                  <div className="batch-done-actions">
                    <button
                      type="button"
                      className="btn ghost"
                      onClick={() => {
                        // The parent's durable tracker is already armed (via
                        // onPublishInFlight). Leave the compose page so it, not
                        // this now-hidden overlay, announces the outcome — and so
                        // the finished post isn't left sitting in the form. Fall
                        // back to just clearing the overlay if no handler is wired.
                        stopDispatchPoll();
                        setDispatchPanel(null);
                        onHide?.();
                      }}
                    >
                      Hide — keep publishing
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="muted batch-done-body">
                    {dispatchPanel.phase === "timeout"
                      ? "It's taking a little longer than usual — it'll appear in your Activity log shortly."
                      : anyHardFail
                        ? "Some platforms didn't go out. Open your Activity log to see why and re-post just those."
                        : "It's out. Every post — now and later — lives in your Activity log."}
                  </p>
                  <div className="batch-done-actions">
                    {onViewActivity && (
                      <button
                        type="button"
                        className="btn primary"
                        onClick={() => {
                          // Full success: clear the draft so a return finds a blank
                          // composer. A failure keeps it, so a Re-post has the draft.
                          if (allOk) resetComposer();
                          else setDispatchPanel(null);
                          onViewActivity();
                        }}
                      >
                        {anyHardFail ? "See what failed" : "View in Activity"}
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn ghost"
                      onClick={() => {
                        if (allOk) resetComposer();
                        else setDispatchPanel(null);
                      }}
                    >
                      {allOk ? "Post another" : "Close"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        );
      })()}

      {/* Out of credits — say so plainly and offer a top-up, so the disabled
          button below isn't a dead end (#4). */}
      {insufficientCredits && (
        <div className="credit-warn" role="status">
          <span>
            This post needs {formatCredits(creditsNeeded)} credits but you have{" "}
            {formatCredits(creditsBalance ?? 0)}. Upgrade for monthly credits, or buy a one-time
            top-up.
          </span>
          {onBuyCredits && (
            <div className="credit-warn-actions">
              <button type="button" className="btn primary small" onClick={onBuyCredits}>
                Upgrade plan
              </button>
              <button type="button" className="btn ghost small" onClick={onBuyCredits}>
                Buy credits
              </button>
            </div>
          )}
        </div>
      )}

      {/* Proactive cost — shown when there's enough, so credits are a decision
          input rather than only a wall you hit. Bigger platforms cost more. */}
      {!insufficientCredits &&
        typeof creditsBalance === "number" &&
        selected.size > 0 &&
        creditsNeeded > 0 && (
          <p className="muted small credit-cost-note">
            Costs {formatCredits(creditsNeeded)} credits ({selected.size} platform
            {selected.size === 1 ? "" : "s"}) · you have {formatCredits(creditsBalance)}.
          </p>
        )}

      {/* ToS §9.1 — the required notice, on the screen where the upload is
          committed, adjacent to the button that commits it. */}
      {youtubeSelected && <YouTubeUploadNotice />}

      {hasMedia && (
        <>
      {/* Compose now, finish later: save the current state as a draft. Loading a
          draft then scheduling/dispatching it deletes it (it's a real post now). */}
      <button
        type="button"
        className="btn wide save-draft-btn"
        disabled={!canSaveDraft}
        onClick={() => void saveDraft()}
      >
        {savingDraft ? "Saving draft…" : loadedDraftId ? "Update draft" : "Save draft"}
      </button>

      <button
        className="primary wide"
        disabled={!canPublish || dispatchInFlight}
        onClick={publish}
        title={disabledReason ?? undefined}
      >
        {busy || dispatchInFlight
          ? editing
            ? "Saving…"
            : scheduling
              ? "Scheduling…"
              : "Firing…"
          : editing
            ? "Save changes"
            : scheduling
              ? `Schedule for ${selected.size || "…"} platform${selected.size === 1 ? "" : "s"}`
              : `Fire to ${selected.size || "…"} platform${selected.size === 1 ? "" : "s"}`}
      </button>
      {/* Text hint mirrors the button's title so the reason is visible, not just
          on hover (#16). Suppressed once a publish is under way. */}
      {disabledReason && !dispatchInFlight && (
        <p className="muted small dispatch-hint">{disabledReason}</p>
      )}
        </>
      )}
      </>
      )}

      {scheduleDone && (
        <div
          className="batch-done-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label="Post scheduled"
          onClick={() => setScheduleDone(null)}
        >
          <div className="batch-done" onClick={(e) => e.stopPropagation()}>
            <div className="batch-done-icon ok">✓</div>
            <h3 className="batch-done-title">
              {scheduleDone.rescheduled ? "Post rescheduled" : "Post scheduled"}
            </h3>
            <p className="muted batch-done-body">
              Going out to {scheduleDone.platforms} platform
              {scheduleDone.platforms === 1 ? "" : "s"} on{" "}
              <strong>{scheduleDone.when}</strong>. You can see it — and move or edit it — on your
              calendar any time before then. Nothing else to do.
            </p>
            <div className="batch-done-actions">
              {onViewCalendar && (
                <button
                  type="button"
                  className="btn primary"
                  onClick={() => {
                    setScheduleDone(null);
                    onViewCalendar();
                  }}
                >
                  View on calendar
                </button>
              )}
              <button
                type="button"
                className="btn ghost"
                onClick={() => setScheduleDone(null)}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Live batch progress — a grey-out overlay with a filling bar as each post
          lands, so a folder of posts shows real motion instead of a "Working…"
          button. The completion popup (batchComplete) takes over when it's done. */}
      {batchRunning && (() => {
        const total = imported.length;
        const processed = imported.filter((p) => {
          const s = batchStatus[p.folder]?.state;
          return s === "done" || s === "failed";
        }).length;
        const failedNow = imported.filter((p) => batchStatus[p.folder]?.state === "failed").length;
        const pct = total ? Math.round((processed / total) * 100) : 0;
        return (
          <div className="batch-done-backdrop" role="dialog" aria-modal="true" aria-label="Scheduling posts">
            <div className="batch-done publish-live">
              <div className="batch-done-spinner" aria-hidden="true" />
              <h3 className="batch-done-title">Firing your broadside…</h3>
              <div className="batch-progress" role="status" aria-live="polite">
                <div className="batch-progress-track">
                  <span className="batch-progress-fill" style={{ width: `${pct}%` }} />
                </div>
                <div className="batch-progress-count muted small">
                  {processed} of {total} done
                  {failedNow > 0 ? ` · ${failedNow} need attention` : ""}
                </div>
              </div>
              <p className="muted batch-done-body">
                Sit tight — this only takes a moment, and you'll get a summary when it's done.
              </p>
            </div>
          </div>
        );
      })()}

      {batchComplete && (
        <div
          className="batch-done-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label="Batch complete"
          onClick={() => setBatchComplete(null)}
        >
          <div className="batch-done" onClick={(e) => e.stopPropagation()}>
            <div className={`batch-done-icon ${batchComplete.failed ? "warn" : "ok"}`}>
              {batchComplete.failed ? "!" : "✓"}
            </div>
            <h3 className="batch-done-title">
              {batchComplete.failed === 0
                ? `All ${batchComplete.ok} post${batchComplete.ok === 1 ? "" : "s"} scheduled`
                : `${batchComplete.ok} scheduled · ${batchComplete.failed} need${
                    batchComplete.failed === 1 ? "s" : ""
                  } attention`}
            </h3>
            <p className="muted batch-done-body">
              {batchComplete.failed === 0
                ? "They're all on your calendar. Want to see the full record? Every post — now and later — lives in your Activity log, so you can check what went out any time."
                : "The scheduled posts are on your calendar. Each failure is saved in your Activity log with a suggested fix — take a look to see what to do next. Nothing here disappears when you dismiss this."}
            </p>
            <div className="batch-done-actions">
              {onViewActivity && (
                <button
                  type="button"
                  className="btn primary"
                  onClick={() => {
                    setBatchComplete(null);
                    onViewActivity();
                  }}
                >
                  {batchComplete.failed > 0 ? "See what failed" : "Open Activity log"}
                </button>
              )}
              <button
                type="button"
                className="btn ghost"
                onClick={() => setBatchComplete(null)}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
